// client/src/pages/School_Admin/Academics/EvaluationControlCenterPage.js
import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Chip,
  Tabs,
  Tab,
  CircularProgress,
  Collapse,
  Tooltip,
  IconButton,
  TextField,
  Button,
  MenuItem,
  Snackbar,
  Alert,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  Checkbox,
  FormControlLabel,
  Table,
  TableHead,
  TableBody,
  TableCell,
  TableRow,
  TableContainer,
  Paper,
  List,
  ListItem,
  ListItemText
} from '@mui/material';
import AppLoader from '../../../components/AppLoader';
import { usePhase } from '../../../contexts/PhaseContext';
import { getCurrentAcademicYearId } from '../../../utils/academicYearContext';
import { fetchJson } from '../../../utils/fetchJson';
import usePhaseClasses from '../../../hooks/usePhaseClasses';
import DownloadIcon from '@mui/icons-material/Download';
import StudentAvatar from '../../../components/Parent/StudentAvatar';
import { UserContext } from '../../../contexts/UserContext';
// The pass rule lives in packages/core and is generated into this copy, so the
// phone, the report card and this sheet cannot disagree about what a pass is.
import { isPass, DEFAULT_PASS_MARK } from '../../../utils/parentAverages.generated';

const seqOptions = [1,2,3,4,5,6];

// WHERE THE API IS, at module scope.
//
// The page already resolves this inside a component (`buildApiUrl`), but a
// plain <a href> in a row component cannot reach a useCallback several
// components away — and the download has to be a real link rather than a
// fetch, so the browser gives a proper save dialog and progress rather than a
// blob assembled in memory.
const API_BASE = (
  process.env.REACT_APP_API_BASE_URL
  || process.env.VITE_API_BASE_URL
  || process.env.NEXT_PUBLIC_API_BASE_URL
  || ''
).trim().replace(/\/+$/, '');
const termOptions = [1,2,3];

const getNurseryRubricScale = (t) => [
  { value: 1, emoji: '😢', label: t('evaluationControlCenter.nurseryRubric.needsSupport'), bg: '#ffebee', bd: '#e53935', text: '#b71c1c' },
  { value: 2, emoji: '😕', label: t('evaluationControlCenter.nurseryRubric.developing'), bg: '#fff3e0', bd: '#fb8c00', text: '#e65100' },
  { value: 3, emoji: '🙂', label: t('evaluationControlCenter.nurseryRubric.satisfactory'), bg: '#fffde7', bd: '#fdd835', text: '#f57f17' },
  { value: 4, emoji: '😃', label: t('evaluationControlCenter.nurseryRubric.good'), bg: '#f1f8e9', bd: '#7cb342', text: '#33691e' },
  { value: 5, emoji: '🌟', label: t('evaluationControlCenter.nurseryRubric.excellent'), bg: '#e8f5e9', bd: '#43a047', text: '#1b5e20' },
];

const renderNurseryRubricBadge = (g, size = 34) => (
  <Box
    sx={{
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: size,
      height: size,
      borderRadius: '50%',
      bgcolor: g.bg,
      border: `2px solid ${g.bd}`,
      color: g.text,
      fontSize: Math.round(size * 0.53),
      lineHeight: 1,
      mx: 'auto'
    }}
  >
    {g.emoji}
  </Box>
);

const levelLabel = (t, lvl) => {
  const tr = typeof t === 'function' ? t : (k) => k;
  const v = (lvl || '').toString();
  if (v === 'nursery') return tr('evaluationControlCenter.levelLabels.nursery');
  if (v === 'primary') return tr('evaluationControlCenter.levelLabels.primary');
  if (v === 'first_cycle') return tr('evaluationControlCenter.levelLabels.firstCycle');
  if (v === 'second_cycle') return tr('evaluationControlCenter.levelLabels.secondCycle');
  return v || tr('evaluationControlCenter.levelLabels.unknown');
};

const evaluationOptionLabel = (t, ev) => {
  if (!ev) return '';
  const type = (ev.type || '').toString().toLowerCase();
  const seq = ev.sequenceNo ?? ev.sequenceNumber ?? ev.number ?? null;
  const mock = ev.mockNo ?? ev.mockNumber ?? ev.number ?? null;
  if (type === 'sequence' && seq != null) return `${t('evaluationControlCenter.setupTypesManager.sequence')} ${seq}`;
  if (type === 'mock' && mock != null) return `Mock ${mock}`;
  if (type === 'term' && ev.termNo != null) return `Term ${ev.termNo}`;
  return ev.label || ev.name || (ev.id != null ? String(ev.id) : '');
};

function PassAverageSettings() {
  const { t } = useTranslation();
  const [passAverage, setPassAverage] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const resp = await fetchJson('/school-setup-and-configuration/school-settings', { method: 'GET' });
      const school = (resp && (resp.data?.school || resp.school || resp.data || resp)) || {};
      const value = school?.passAverage;
      if (value === undefined || value === null || value === '') {
        setPassAverage('');
      } else {
        setPassAverage(String(value));
      }
    } catch (e) {
      setMsg({ type: 'error', text: e?.message || t('evaluationControlCenter.passAverage.loadFail') });
    } finally {
      setLoading(false);
    }
  }, []);

  const onSave = useCallback(async () => {
    if (passAverage === '') {
      setMsg({ type: 'error', text: t('evaluationControlCenter.passAverage.required') });
      return;
    }
    setSaving(true);
    try {
      await fetchJson('/school-setup-and-configuration/school-settings', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: {
          year: {
            passAverage: Number(passAverage)
          }
        }
      });

      setMsg({ type: 'success', text: t('evaluationControlCenter.passAverage.updated') });
      await load();
    } catch (e) {
      setMsg({ type: 'error', text: e?.message || t('evaluationControlCenter.passAverage.updateFail') });
    } finally {
      setSaving(false);
    }
  }, [passAverage, load]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <Card>
      <CardContent>
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'flex-start', sm: 'center' }, gap: 1, mb: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>{t('evaluationControlCenter.passAverage.title')}</Typography>
          <Button variant="contained" onClick={onSave} disabled={saving || loading}>
            {saving ? t('evaluationControlCenter.saving') : t('evaluationControlCenter.save')}
          </Button>
        </Box>

        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              type="number"
              inputProps={{ step: 0.1, min: 0, max: 20 }}
              label={t('evaluationControlCenter.passAverage.label')}
              value={passAverage}
              onChange={(e) => setPassAverage(e.target.value)}
              disabled={saving || loading}
            />
          </Grid>
        </Grid>
      </CardContent>
      <Snackbar open={!!msg} autoHideDuration={3000} onClose={() => setMsg(null)}>
        {msg && <Alert severity={msg.type} onClose={() => setMsg(null)}>{msg.text}</Alert>}
      </Snackbar>
    </Card>
  );
}

function PromotionCriteriaSettings() {
  const { t, i18n } = useTranslation();
  const { activePhase } = usePhase();
  const { classes, loading: classesLoading } = usePhaseClasses();

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);

  const [subjects, setSubjects] = useState([]);
  const [selectedClassIds, setSelectedClassIds] = useState([]);

  // Criteria can be set for particular CLASSES or for a whole LEVEL.
  //
  // The level scope is not a convenience — it is the only correct way to
  // express nursery. Nursery and primary sit in the same phase but are
  // different systems: nursery is assessed on competencies, not on an average
  // out of 20, so a numeric pass mark is meaningless there. Keying on level
  // means every nursery class inherits the right rules, including classes
  // created later, which a per-class list silently misses.
  //
  // Precedence matters and is stated in the UI: the promotion engine resolves
  // CLASS first, then LEVEL, then the phase default. So a per-class entry
  // overrides the level — which is exactly how a well-meaning bulk apply used
  // to wipe out nursery's rules without saying so.
  const [scope, setScope] = useState('level');
  const [selectedLevel, setSelectedLevel] = useState('');

  // Only levels that actually have classes in this phase.
  const levelOptions = useMemo(() => {
    const set = new Set();
    for (const c of classes || []) {
      const lvl = String(c?.level || '').trim().toLowerCase();
      if (lvl) set.add(lvl);
    }
    return Array.from(set).sort();
  }, [classes]);

  // Nursery is a different system: children are assessed on a 1-5 developmental
  // scale describing the support they need, so an average out of 20 and a
  // "failed subject" are both meaningless there. Primary, first cycle and second
  // cycle all share the mark format.
  const criteriaFormat = useCallback(
    (lvl) => (String(lvl || '').trim().toLowerCase() === 'nursery' ? 'developmental' : 'marks'),
    []
  );

  // Everything below the level picker narrows to that level, so a secondary
  // admin setting second-cycle rules never scans past nursery classes.
  const levelClasses = useMemo(
    () => (classes || []).filter(
      (c) => String(c?.level || '').trim().toLowerCase() === String(selectedLevel || '').trim().toLowerCase()
    ),
    [classes, selectedLevel]
  );
  const levelSubjects = useMemo(
    () => (subjects || []).filter(
      (x) => String(x?.level || '').trim().toLowerCase() === String(selectedLevel || '').trim().toLowerCase()
    ),
    [subjects, selectedLevel]
  );
  const activeFormat = criteriaFormat(selectedLevel);

  const [averageEnabled, setAverageEnabled] = useState(true);
  const [minAverage, setMinAverage] = useState('');

  const [coreEnabled, setCoreEnabled] = useState(false);
  const [coreSubjectIds, setCoreSubjectIds] = useState([]);
  const [corePassThreshold, setCorePassThreshold] = useState('');

  const [maxFailedEnabled, setMaxFailedEnabled] = useState(false);
  const [maxFailed, setMaxFailed] = useState('');
  const [failThreshold, setFailThreshold] = useState('');

  const [disciplineEnabled, setDisciplineEnabled] = useState(false);

  // Nursery only — the 1-5 developmental rule.
  const [developmentalEnabled, setDevelopmentalEnabled] = useState(false);
  const [minRating, setMinRating] = useState('');
  const [maxWarnings, setMaxWarnings] = useState('');
  const [maxUnjustifiedAbsenceMinutes, setMaxUnjustifiedAbsenceMinutes] = useState('');

  const [loadedPromotionCriteria, setLoadedPromotionCriteria] = useState(null);

  const phaseKey = (activePhase || 'default').toString().trim().toLowerCase() || 'default';

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const resp = await fetchJson('/school-setup-and-configuration/school-settings', { method: 'GET' });
      const school = (resp && (resp.data?.school || resp.school || resp.data || resp)) || {};
      const promo = school?.promotionCriteria || school?.customFields?.promotionCriteria || null;
      setLoadedPromotionCriteria(promo && typeof promo === 'object' ? promo : null);
    } catch (e) {
      setMsg({ type: 'error', text: e?.message || t('evaluationControlCenter.promotionCriteria.loadFail') });
      setLoadedPromotionCriteria(null);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadSubjects = useCallback(async () => {
    try {
      const rows = await fetchJson('/api/subjects', { method: 'GET' });
      setSubjects(Array.isArray(rows) ? rows : []);
    } catch (_) {
      setSubjects([]);
    }
  }, []);

  useEffect(() => {
    load();
    loadSubjects();
  }, [load, loadSubjects]);

  const currentConfigForSelectedClass = useMemo(() => {
    if (!loadedPromotionCriteria || typeof loadedPromotionCriteria !== 'object') return null;
    const phaseCfg = loadedPromotionCriteria[phaseKey] || loadedPromotionCriteria.default || null;
    if (!phaseCfg || typeof phaseCfg !== 'object') return null;
    if (scope === 'level') {
      const byLevel = phaseCfg.levels && typeof phaseCfg.levels === 'object' ? phaseCfg.levels : {};
      if (selectedLevel && byLevel[selectedLevel]) return byLevel[selectedLevel];
      return phaseCfg.default && typeof phaseCfg.default === 'object' ? phaseCfg.default : null;
    }
    const byClass = phaseCfg.classes && typeof phaseCfg.classes === 'object' ? phaseCfg.classes : {};
    const firstId = selectedClassIds[0];
    if (firstId && byClass[String(firstId)]) return byClass[String(firstId)];
    return phaseCfg.default && typeof phaseCfg.default === 'object' ? phaseCfg.default : null;
  }, [loadedPromotionCriteria, phaseKey, selectedClassIds, scope, selectedLevel]);

  useEffect(() => {
    const cfg = currentConfigForSelectedClass;
    if (!cfg) {
      setAverageEnabled(true);
      setMinAverage('');
      setCoreEnabled(false);
      setCoreSubjectIds([]);
      setCorePassThreshold('');
      setMaxFailedEnabled(false);
      setMaxFailed('');
      setFailThreshold('');
      setDisciplineEnabled(false);
      setMaxWarnings('');
      setMaxUnjustifiedAbsenceMinutes('');
      setDevelopmentalEnabled(false);
      setMinRating('');
      return;
    }

    setDevelopmentalEnabled(!!cfg?.developmental?.enabled);
    setMinRating(cfg?.developmental?.minAverageRating == null ? '' : String(cfg.developmental.minAverageRating));

    setAverageEnabled(cfg?.averageRule?.enabled !== false);
    setMinAverage(cfg?.averageRule?.minAverage == null ? '' : String(cfg.averageRule.minAverage));

    setCoreEnabled(!!cfg?.coreSubjects?.enabled);
    setCoreSubjectIds(Array.isArray(cfg?.coreSubjects?.subjectIds) ? cfg.coreSubjects.subjectIds.map(String) : []);
    setCorePassThreshold(cfg?.coreSubjects?.passThreshold == null ? '' : String(cfg.coreSubjects.passThreshold));

    setMaxFailedEnabled(!!cfg?.maxFailedSubjects?.enabled);
    setMaxFailed(cfg?.maxFailedSubjects?.maxFailed == null ? '' : String(cfg.maxFailedSubjects.maxFailed));
    setFailThreshold(cfg?.maxFailedSubjects?.failThreshold == null ? '' : String(cfg.maxFailedSubjects.failThreshold));

    setDisciplineEnabled(!!cfg?.discipline?.enabled);
    setMaxWarnings(cfg?.discipline?.maxWarnings == null ? '' : String(cfg.discipline.maxWarnings));
    setMaxUnjustifiedAbsenceMinutes(cfg?.discipline?.maxUnjustifiedAbsenceMinutes == null ? '' : String(cfg.discipline.maxUnjustifiedAbsenceMinutes));
  }, [currentConfigForSelectedClass]);

  const onSave = useCallback(async () => {
    if (scope === 'level' && !selectedLevel) {
      setMsg({ type: 'error', text: t('evaluationControlCenter.promotionCriteria.selectLevel', 'Choose a level first.') });
      return;
    }
    if (scope === 'class' && !selectedClassIds.length) {
      setMsg({ type: 'error', text: t('evaluationControlCenter.promotionCriteria.selectClass') });
      return;
    }
    setSaving(true);
    try {
      const toNumOrNull = (v) => {
        if (v === '' || v === null || v === undefined) return null;
        const n = Number(v);
        return Number.isFinite(n) ? n : null;
      };
      const toIntOrNull = (v) => {
        if (v === '' || v === null || v === undefined) return null;
        const n = parseInt(String(v), 10);
        return Number.isFinite(n) ? n : null;
      };

      // The block is written to the level's OWN format. The mark rules are not
      // merely hidden for nursery — they are left out, so the promotion engine
      // cannot later read a leftover pass mark for a three-year-old.
      const cfg = criteriaFormat(selectedLevel) === 'developmental'
        ? {
          developmental: {
            enabled: !!developmentalEnabled,
            minAverageRating: toNumOrNull(minRating)
          }
        }
        : {
          averageRule: {
            enabled: !!averageEnabled,
            minAverage: toNumOrNull(minAverage)
          },
          coreSubjects: {
            enabled: !!coreEnabled,
            subjectIds: (coreSubjectIds || []).map(String),
            passThreshold: toNumOrNull(corePassThreshold)
          },
          maxFailedSubjects: {
            enabled: !!maxFailedEnabled,
            maxFailed: toIntOrNull(maxFailed),
            failThreshold: toNumOrNull(failThreshold)
          },
          discipline: {
            enabled: !!disciplineEnabled,
            maxWarnings: toIntOrNull(maxWarnings),
            maxUnjustifiedAbsenceMinutes: toIntOrNull(maxUnjustifiedAbsenceMinutes)
          }
        };

      // Level scope writes levels[level]; class scope writes classes[id].
      const scoped = scope === 'level'
        ? { levels: { [selectedLevel]: cfg } }
        : { classes: selectedClassIds.reduce((acc, cid) => { acc[String(cid)] = cfg; return acc; }, {}) };

      await fetchJson('/school-setup-and-configuration/school-settings', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: {
          year: {
            promotionCriteria: {
              [phaseKey]: scoped
            }
          }
        }
      });

      setMsg({
        type: 'success',
        text: scope === 'level'
          ? t('evaluationControlCenter.promotionCriteria.levelUpdated', 'Criteria saved for every class at this level.')
          : t('evaluationControlCenter.promotionCriteria.bulkUpdated', { count: selectedClassIds.length })
      });
      await load();
    } catch (e) {
      setMsg({ type: 'error', text: e?.message || t('evaluationControlCenter.promotionCriteria.updateFail') });
    } finally {
      setSaving(false);
    }
  }, [scope, selectedLevel, selectedClassIds, averageEnabled, minAverage, coreEnabled, coreSubjectIds, corePassThreshold, maxFailedEnabled, maxFailed, failThreshold, disciplineEnabled, maxWarnings, maxUnjustifiedAbsenceMinutes, developmentalEnabled, minRating, criteriaFormat, load, phaseKey]);

  // Core subjects are offered from the CHOSEN LEVEL only. A primary core
  // subject cannot be a second-cycle requirement, and offering the whole
  // school's list made the picker unusable and the mistake easy to make.
  const subjectOptions = useMemo(() => {
    const rows = Array.isArray(levelSubjects) ? levelSubjects : [];
    return rows
      .map((s) => ({ id: String(s.id), name: s.name || s.subjectName || t('evaluationControlCenter.common.subjectWithId', { id: s.id }) }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [levelSubjects, i18n.language]);

  return (
    <Card>
      <CardContent>
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'flex-start', sm: 'center' }, gap: 1, mb: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>{t('evaluationControlCenter.promotionCriteria.title')}</Typography>
          <Button variant="contained" onClick={onSave} disabled={saving || loading || classesLoading}>
            {saving ? t('evaluationControlCenter.saving') : t('evaluationControlCenter.save')}
          </Button>
        </Box>

        <Alert severity="info" sx={{ mb: 2 }}>
          {t('evaluationControlCenter.promotionCriteria.precedence',
            'Criteria are resolved class first, then level, then the phase default. A rule set for a particular class therefore overrides the rule set for its level.')}
        </Alert>

        <Grid container spacing={2} alignItems="center" sx={{ mb: 1 }}>
          <Grid item xs={12} md={4}>
            <TextField
              select
              fullWidth
              label={t('evaluationControlCenter.promotionCriteria.applyTo', 'Apply to')}
              value={scope}
              onChange={(e) => setScope(e.target.value)}
              disabled={loading || classesLoading}
            >
              <MenuItem value="class">
                {t('evaluationControlCenter.promotionCriteria.scopeClass', 'Particular classes')}
              </MenuItem>
              <MenuItem value="level">
                {t('evaluationControlCenter.promotionCriteria.scopeLevel', 'A whole level')}
              </MenuItem>
            </TextField>
          </Grid>
          {scope === 'level' && (
            <Grid item xs={12} md={4}>
              <TextField
                select
                fullWidth
                label={t('evaluationControlCenter.promotionCriteria.level', 'Level')}
                value={selectedLevel}
                onChange={(e) => setSelectedLevel(e.target.value)}
                disabled={loading || classesLoading}
                helperText={t('evaluationControlCenter.promotionCriteria.levelHelper',
                  'Applies to every class at this level, including ones created later.')}
              >
                {levelOptions.map((lvl) => (
                  <MenuItem key={lvl} value={lvl}>{levelLabel(t, lvl)}</MenuItem>
                ))}
              </TextField>
            </Grid>
          )}
        </Grid>

        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={8} sx={{ display: scope === 'level' ? 'none' : 'block' }}>
            <TextField
              select
              fullWidth
              label={t('evaluationControlCenter.common.class')}
              value={selectedClassIds}
              onChange={(e) => {
                const val = e.target.value;
                setSelectedClassIds(typeof val === 'string' ? val.split(',') : val);
              }}
              disabled={loading || classesLoading}
              helperText={t('evaluationControlCenter.promotionCriteria.bulkHelper', 'Select one or more classes to apply criteria in bulk')}
              SelectProps={{ multiple: true, renderValue: (sel) => sel.length === (classes || []).length ? t('evaluationControlCenter.promotionCriteria.allClasses', 'All Classes') : `${sel.length} ${t('evaluationControlCenter.promotionCriteria.classesSelected', 'class(es) selected')}` }}
            >
              {(levelClasses || []).map((c) => (
                <MenuItem key={c.id} value={String(c.id)}>
                  <Checkbox checked={selectedClassIds.includes(String(c.id))} size="small" sx={{ mr: 1 }} />
                  {c.name}{c.level ? ` (${levelLabel(t, c.level)})` : ''}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} md={4}>
            <Button
              variant="outlined"
              size="small"
              onClick={() => {
                const allIds = (classes || []).map((c) => String(c.id));
                setSelectedClassIds(selectedClassIds.length === allIds.length ? [] : allIds);
              }}
              disabled={loading || classesLoading || !(classes || []).length}
            >
              {selectedClassIds.length === (classes || []).length
                ? t('evaluationControlCenter.promotionCriteria.deselectAll', 'Deselect All')
                : t('evaluationControlCenter.promotionCriteria.selectAll', 'Select All Classes')}
            </Button>
          </Grid>
        </Grid>

        <Divider sx={{ my: 2 }} />

        {activeFormat === 'developmental' ? (
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Alert severity="info">
                {t('evaluationControlCenter.promotionCriteria.nurseryNote',
                  'Nursery children are assessed on a 1–5 developmental scale describing the support each child needs, not on marks out of 20. An average and a "failed subject" do not apply here, so this level has its own rule.')}
              </Alert>
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={<Checkbox checked={developmentalEnabled} onChange={(e) => setDevelopmentalEnabled(e.target.checked)} />}
                label={t('evaluationControlCenter.promotionCriteria.requireMinRating', 'Require a minimum developmental rating')}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                type="number"
                inputProps={{ step: 0.1, min: 1, max: 5 }}
                label={t('evaluationControlCenter.promotionCriteria.minRating', 'Minimum average rating (1–5)')}
                value={minRating}
                onChange={(e) => setMinRating(e.target.value)}
                disabled={!developmentalEnabled}
                helperText={t('evaluationControlCenter.promotionCriteria.minRatingHelper',
                  '5 highly capable · 4 capable · 3 coping · 2 needs support · 1 significant difficulty. A child with no ratings recorded is not held back by this rule.')}
              />
            </Grid>
            {!developmentalEnabled && (
              <Grid item xs={12}>
                <Typography variant="body2" color="text.secondary">
                  {t('evaluationControlCenter.promotionCriteria.nurseryAllProgress',
                    'Every child progresses unless the class teacher holds them back.')}
                </Typography>
              </Grid>
            )}
          </Grid>
        ) : (
        <>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <FormControlLabel
              control={<Checkbox checked={averageEnabled} onChange={(e) => setAverageEnabled(e.target.checked)} />}
              label={t('evaluationControlCenter.promotionCriteria.requireMinAvg')}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              type="number"
              inputProps={{ step: 0.1, min: 0, max: 20 }}
              label={t('evaluationControlCenter.promotionCriteria.minAvg')}
              value={minAverage}
              onChange={(e) => setMinAverage(e.target.value)}
              disabled={!averageEnabled}
              helperText={t('evaluationControlCenter.promotionCriteria.useSchoolAvg')}
            />
          </Grid>
        </Grid>

        <Divider sx={{ my: 2 }} />

        <Grid container spacing={2}>
          <Grid item xs={12}>
            <FormControlLabel
              control={<Checkbox checked={coreEnabled} onChange={(e) => setCoreEnabled(e.target.checked)} />}
              label={t('evaluationControlCenter.promotionCriteria.coreSubjectsMustPass')}
            />
          </Grid>
          <Grid item xs={12} md={8}>
            <TextField
              select
              SelectProps={{ multiple: true, value: coreSubjectIds, onChange: (e) => setCoreSubjectIds(e.target.value) }}
              fullWidth
              label={t('evaluationControlCenter.promotionCriteria.coreSubjects')}
              disabled={!coreEnabled}
              helperText={t('evaluationControlCenter.promotionCriteria.selectCoreSubjects')}
            >
              {subjectOptions.map((s) => (
                <MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              type="number"
              inputProps={{ step: 0.1, min: 0, max: 20 }}
              label={t('evaluationControlCenter.promotionCriteria.corePassThreshold')}
              value={corePassThreshold}
              onChange={(e) => setCorePassThreshold(e.target.value)}
              disabled={!coreEnabled}
              helperText={t('evaluationControlCenter.promotionCriteria.usePassAvg')}
            />
          </Grid>
        </Grid>

        <Divider sx={{ my: 2 }} />

        <Grid container spacing={2}>
          <Grid item xs={12}>
            <FormControlLabel
              control={<Checkbox checked={maxFailedEnabled} onChange={(e) => setMaxFailedEnabled(e.target.checked)} />}
              label={t('evaluationControlCenter.promotionCriteria.limitFailedSubjects')}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              type="number"
              inputProps={{ step: 1, min: 0 }}
              label={t('evaluationControlCenter.promotionCriteria.maxFailed')}
              value={maxFailed}
              onChange={(e) => setMaxFailed(e.target.value)}
              disabled={!maxFailedEnabled}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              type="number"
              inputProps={{ step: 0.1, min: 0, max: 20 }}
              label={t('evaluationControlCenter.promotionCriteria.failThreshold')}
              value={failThreshold}
              onChange={(e) => setFailThreshold(e.target.value)}
              disabled={!maxFailedEnabled}
              helperText={t('evaluationControlCenter.promotionCriteria.failThresholdHelper')}
            />
          </Grid>
        </Grid>

        <Divider sx={{ my: 2 }} />

        <Grid container spacing={2}>
          <Grid item xs={12}>
            <FormControlLabel
              control={<Checkbox checked={disciplineEnabled} onChange={(e) => setDisciplineEnabled(e.target.checked)} />}
              label={t('evaluationControlCenter.promotionCriteria.disciplineGates')}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              type="number"
              inputProps={{ step: 1, min: 0 }}
              label={t('evaluationControlCenter.promotionCriteria.maxWarnings')}
              value={maxWarnings}
              onChange={(e) => setMaxWarnings(e.target.value)}
              disabled={!disciplineEnabled}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              type="number"
              inputProps={{ step: 1, min: 0 }}
              label={t('evaluationControlCenter.promotionCriteria.maxUnjustifiedAbsenceMinutes')}
              value={maxUnjustifiedAbsenceMinutes}
              onChange={(e) => setMaxUnjustifiedAbsenceMinutes(e.target.value)}
              disabled={!disciplineEnabled}
            />
          </Grid>
        </Grid>
        </>
        )}
      </CardContent>
      <Snackbar open={!!msg} autoHideDuration={3000} onClose={() => setMsg(null)}>
        {msg && <Alert severity={msg.type} onClose={() => setMsg(null)}>{msg.text}</Alert>}
      </Snackbar>
    </Card>
  );
}

function MeritThresholdSettings() {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);
  const [honourRoll, setHonourRoll] = useState('');
  const [encouragement, setEncouragement] = useState('');
  const [congratulations, setCongratulations] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const resp = await fetchJson('/school-setup-and-configuration/school-settings', { method: 'GET' });
      const school = (resp && (resp.data?.school || resp.school || resp.data || resp)) || {};
      const thresholds = school?.meritThresholds || school?.customFields?.meritThresholds || {};
      setHonourRoll(thresholds?.honourRoll == null ? '' : String(thresholds.honourRoll));
      setEncouragement(thresholds?.encouragement == null ? '' : String(thresholds.encouragement));
      setCongratulations(thresholds?.congratulations == null ? '' : String(thresholds.congratulations));
    } catch (e) {
      setMsg({ type: 'error', text: e?.message || t('evaluationControlCenter.meritThresholds.loadFail') });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const onSave = useCallback(async () => {
    setSaving(true);
    try {
      const toNum = (s) => {
        const n = Number(s);
        return Number.isFinite(n) ? n : null;
      };
      await fetchJson('/school-setup-and-configuration/school-settings', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: {
          year: {
            meritThresholds: {
              honourRoll: toNum(honourRoll),
              encouragement: toNum(encouragement),
              congratulations: toNum(congratulations)
            }
          }
        }
      });
      setMsg({ type: 'success', text: t('evaluationControlCenter.meritThresholds.updated') });
      load();
    } catch (e) {
      setMsg({ type: 'error', text: e?.message || t('evaluationControlCenter.meritThresholds.updateFail') });
    } finally {
      setSaving(false);
    }
  }, [honourRoll, encouragement, congratulations, load]);

  return (
    <Card>
      <CardContent>
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'flex-start', sm: 'center' }, gap: 1, mb: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>{t('evaluationControlCenter.meritThresholds.title')}</Typography>
          <Button variant="contained" onClick={onSave} disabled={loading || saving}>
            {saving ? t('evaluationControlCenter.saving') : t('evaluationControlCenter.save')}
          </Button>
        </Box>
        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label={t('evaluationControlCenter.meritThresholds.honourRoll')}
              value={honourRoll}
              onChange={(e) => setHonourRoll(e.target.value)}
              helperText={t('evaluationControlCenter.meritThresholds.honourRollHelper')}
              disabled={loading}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              type="number"
              inputProps={{ step: 0.1, min: 0, max: 20 }}
              label={t('evaluationControlCenter.meritThresholds.congratulations')}
              value={congratulations}
              onChange={(e) => setCongratulations(e.target.value)}
              helperText={t('evaluationControlCenter.meritThresholds.congratulationsHelper')}
              disabled={loading}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              type="number"
              inputProps={{ step: 0.1, min: 0, max: 20 }}
              label={t('evaluationControlCenter.meritThresholds.encouragement')}
              value={encouragement}
              onChange={(e) => setEncouragement(e.target.value)}
              helperText={t('evaluationControlCenter.meritThresholds.encouragementHelper')}
              disabled={loading}
            />
          </Grid>
        </Grid>
      </CardContent>
      <Snackbar open={!!msg} autoHideDuration={3000} onClose={() => setMsg(null)}>
        {msg && <Alert severity={msg.type} onClose={() => setMsg(null)}>{msg.text}</Alert>}
      </Snackbar>
    </Card>
  );
}

function CreateSequence() {
  const { t } = useTranslation();
  const [sequenceNo, setSequenceNo] = useState(1);
  const [classId, setClassId] = useState('');
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);

  const onCreate = useCallback(async () => {
    setSaving(true);
    try {
      const body = { sequenceNo, classId: classId || null };
      const res = await fetchJson('/api/academics/evaluations/sequence', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body
      });
      setMsg({ type: 'success', text: t('evaluationControlCenter.createSequence.created') });
    } catch (e) {
      setMsg({ type: 'error', text: e?.message || t('evaluationControlCenter.createSequence.createFail') });
    } finally {
      setSaving(false);
    }
  }, [sequenceNo, classId]);

  return (
    <Card>
      <CardContent>
        <Typography variant="h6" gutterBottom>{t('evaluationControlCenter.createSequence.title')}</Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <TextField
              select
              label={t('evaluationControlCenter.createSequence.sequence')}
              value={sequenceNo}
              onChange={(e) => setSequenceNo(Number(e.target.value))}
              fullWidth
            >
              {seqOptions.map(n => <MenuItem key={n} value={n}>{n}</MenuItem>)}
            </TextField>
          </Grid>
          <Grid item xs={12} md={5}>
            <TextField
              label={t('evaluationControlCenter.createSequence.classIdOptional')}
              value={classId}
              onChange={(e) => setClassId(e.target.value)}
              fullWidth
              placeholder={t('evaluationControlCenter.createSequence.example')}
            />
          </Grid>
          <Grid item xs={12} md={3} sx={{ display: 'flex', alignItems: 'center' }}>
            <Button variant="contained" onClick={onCreate} disabled={saving}>
              {saving ? t('evaluationControlCenter.creating') : t('evaluationControlCenter.create')}
            </Button>
          </Grid>
        </Grid>
      </CardContent>
      <Snackbar open={!!msg} autoHideDuration={3000} onClose={() => setMsg(null)}>
        {msg && <Alert severity={msg.type} onClose={() => setMsg(null)}>{msg.text}</Alert>}
      </Snackbar>
    </Card>
  );
}

function SetupTypesManager() {
  const { t } = useTranslation();
  const { activePhase } = usePhase();
  const { classes, loading: classesLoading } = usePhaseClasses({ allowEmptyFallback: false });
  const [loading, setLoading] = useState(false);
  const [types, setTypes] = useState([]);
  const [msg, setMsg] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const phaseId = localStorage.getItem('schoolAdmin_activePhaseId') || localStorage.getItem('activePhaseId') || '';
      const phaseCode = localStorage.getItem('schoolAdmin_activePhase') || localStorage.getItem('activePhase') || '';
      const headers = {};
      if (phaseId) headers['X-Active-Phase-Id'] = String(phaseId);
      if (phaseCode) headers['X-Active-Phase'] = String(phaseCode);
      const data = await fetchJson('/api/academics/evaluations/setup/types', { headers });
      const list = Array.isArray(data?.types) ? data.types : [];
      setTypes(list);
    } catch (e) {
      setMsg({ type: 'error', text: e?.message || t('evaluationControlCenter.setupTypesManager.errors.loadSequences') });
    } finally {
      setLoading(false);
    }
  }, [activePhase]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const handler = () => load();
    window.addEventListener('app:phase:changed', handler);
    window.addEventListener('app:ay:changed', handler);
    return () => {
      window.removeEventListener('app:phase:changed', handler);
      window.removeEventListener('app:ay:changed', handler);
    };
  }, [load]);

  const handleToggle = useCallback(async (type) => {
    const action = type.state === 'open' ? 'close' : 'open';
    try {
      const phaseId = localStorage.getItem('schoolAdmin_activePhaseId') || localStorage.getItem('activePhaseId') || '';
      const phaseCode = localStorage.getItem('schoolAdmin_activePhase') || localStorage.getItem('activePhase') || '';
      const headers = { 'Content-Type': 'application/json' };
      if (phaseId) headers['X-Active-Phase-Id'] = String(phaseId);
      if (phaseCode) headers['X-Active-Phase'] = String(phaseCode);
      await fetchJson(`/api/academics/evaluations/types/${encodeURIComponent(type.id)}/${action}`, { method: 'POST', headers });
      await load();
    } catch (e) {
      setMsg({ type: 'error', text: e?.message || `Failed to ${action} evaluation.` });
    }
  }, [load]);

  return (
    <Card>
      <CardContent>
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'flex-start', sm: 'center' }, gap: 1, mb: 2 }}>
          <Typography variant="h6">{t('evaluationControlCenter.setupTypesManager.title')}</Typography>
          <Button size="small" onClick={load} disabled={loading}>
            {loading ? t('evaluationControlCenter.setupTypesManager.refreshing') : t('evaluationControlCenter.setupTypesManager.refresh')}
          </Button>
        </Box>
        {loading && types.length === 0 ? (
          <Box sx={{ p: 2 }}>
            <AppLoader
              variant="circular3"
              size={56}
              messages={[
                t('evaluationControlCenter.setupTypesManager.loadingEvaluationTypes'),
                t('evaluationControlCenter.setupTypesManager.applyingPhase')
              ]}
            />
          </Box>
        ) : (
          <Grid container spacing={2}>
            {(types.length ? types : []).map((type) => (
              <Grid key={type.id} item xs={12} md={4} lg={3}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="subtitle2" gutterBottom>
                      {type.type === 'sequence'
                        ? t('evaluationControlCenter.setupTypesManager.sequenceLabel', { n: type.number })
                        : (type?.name || t('evaluationControlCenter.setupTypesManager.customLabel', { n: type.number }))}
                    </Typography>
                    <Chip
                      size="small"
                      label={type.state === 'open'
                        ? t('evaluationControlCenter.setupTypesManager.states.open')
                        : t('evaluationControlCenter.setupTypesManager.states.closed')}
                      color={type.state === 'open' ? 'success' : 'default'}
                      sx={{ mr: 1 }}
                    />
                    {type.state === 'open' ? (
                      <Button size="small" color="warning" onClick={() => handleToggle(type)}>Close</Button>
                    ) : (
                      <Button size="small" color="primary" onClick={() => handleToggle(type)}>Open</Button>
                    )}
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                      {t('evaluationControlCenter.setupTypesManager.assignedClasses', { count: type.assignedClassesCount ?? 0 })}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}
      </CardContent>
      <Snackbar open={!!msg} autoHideDuration={3000} onClose={() => setMsg(null)}>
        {msg && <Alert severity={msg.type} onClose={() => setMsg(null)}>{msg.text}</Alert>}
      </Snackbar>
    </Card>
  );
}

function CreateTerm() {
  const { t } = useTranslation();
  const [termNo, setTermNo] = useState(1);
  const [classId, setClassId] = useState('');
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);

  const onCreate = useCallback(async () => {
    setSaving(true);
    try {
      const body = { termNo, classId: classId || null };
      await fetchJson('/api/academics/evaluations/term', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body
      });
      setMsg({ type: 'success', text: t('evaluationControlCenter.setupTypesManager.updated') });
    } catch (e) {
      setMsg({ type: 'error', text: e?.message || t('evaluationControlCenter.createTerm.errors.createFail') });
    } finally {
      setSaving(false);
    }
  }, [termNo, classId]);

  const seqPair = useMemo(() => {
    if (termNo === 1) return t('evaluationControlCenter.createTerm.seqPairs.term1');
    if (termNo === 2) return t('evaluationControlCenter.createTerm.seqPairs.term2');
    return t('evaluationControlCenter.createTerm.seqPairs.term3');
  }, [termNo, t]);

  return (
    <Card>
      <CardContent>
        <Typography variant="h6" gutterBottom>{t('evaluationControlCenter.createTerm.title')}</Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <TextField
              select
              label={t('evaluationControlCenter.setupTypesManager.term')}
              value={termNo}
              onChange={(e) => setTermNo(Number(e.target.value))}
              fullWidth
            >
              {termOptions.map(n => <MenuItem key={n} value={n}>{n}</MenuItem>)}
            </TextField>
          </Grid>
          <Grid item xs={12} md={5}>
            <TextField
              label={t('evaluationControlCenter.createSequence.classIdOptional')}
              value={classId}
              onChange={(e) => setClassId(e.target.value)}
              fullWidth
              placeholder={t('evaluationControlCenter.createSequence.example')}
            />
            <Typography variant="caption" color="text.secondary">{seqPair}</Typography>
          </Grid>
          <Grid item xs={12} md={3} sx={{ display: 'flex', alignItems: 'center' }}>
            <Button variant="contained" onClick={onCreate} disabled={saving}>
              {saving ? t('evaluationControlCenter.creating') : t('evaluationControlCenter.create')}
            </Button>
          </Grid>
        </Grid>
      </CardContent>
      <Snackbar open={!!msg} autoHideDuration={3000} onClose={() => setMsg(null)}>
        {msg && <Alert severity={msg.type} onClose={() => setMsg(null)}>{msg.text}</Alert>}
      </Snackbar>
    </Card>
  );
}

function CreateAnnual() {
  const { t } = useTranslation();
  const [classId, setClassId] = useState('');
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);

  const onCreate = useCallback(async () => {
    setSaving(true);
    try {
      const body = { classId: classId || null };
      await fetchJson('/api/academics/evaluations/annual', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body
      });
      setMsg({ type: 'success', text: t('evaluationControlCenter.setupTypesManager.updated') });
    } catch (e) {
      setMsg({ type: 'error', text: e?.message || t('evaluationControlCenter.createAnnual.errors.createFail') });
    } finally {
      setSaving(false);
    }
  }, [classId]);

  return (
    <Card>
      <CardContent>
        <Typography variant="h6" gutterBottom>{t('evaluationControlCenter.createAnnual.title')}</Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} md={9}>
            <TextField
              label={t('evaluationControlCenter.createSequence.classIdOptional')}
              value={classId}
              onChange={(e) => setClassId(e.target.value)}
              fullWidth
            />
          </Grid>
          <Grid item xs={12} md={3} sx={{ display: 'flex', alignItems: 'center' }}>
            <Button variant="contained" onClick={onCreate} disabled={saving}>
              {saving ? t('evaluationControlCenter.creating') : t('evaluationControlCenter.create')}
            </Button>
          </Grid>
        </Grid>
      </CardContent>
      <Snackbar open={!!msg} autoHideDuration={3000} onClose={() => setMsg(null)}>
        {msg && <Alert severity={msg.type} onClose={() => setMsg(null)}>{msg.text}</Alert>}
      </Snackbar>
    </Card>
  );
}

function AssignmentsManager() {
  const { t } = useTranslation();
  const { classes: phaseClasses = [] } = usePhaseClasses({ allowEmptyFallback: false });
  const [classIds, setClassIds] = useState([]);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);

  const [loadingTypes, setLoadingTypes] = useState(false);
  const [customTypes, setCustomTypes] = useState([]);
  const [typeId, setTypeId] = useState('');

  const [createOpen, setCreateOpen] = useState(false);
  const [createValues, setCreateValues] = useState({ name: '', state: 'closed' });

  const [editOpen, setEditOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [editValues, setEditValues] = useState({ name: '', state: 'closed' });

  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteId, setDeleteId] = useState(null);

  const loadCustomTypes = useCallback(async () => {
    setLoadingTypes(true);
    try {
      const phaseId = localStorage.getItem('schoolAdmin_activePhaseId') || localStorage.getItem('activePhaseId') || '';
      const phaseCode = localStorage.getItem('schoolAdmin_activePhase') || localStorage.getItem('activePhase') || '';
      const headers = {};
      if (phaseId) headers['X-Active-Phase-Id'] = String(phaseId);
      if (phaseCode) headers['X-Active-Phase'] = String(phaseCode);
      const data = await fetchJson('/api/evaluation-types', { method: 'GET', headers });
      const list = Array.isArray(data) ? data : (data?.data || data?.types || []);
      const filtered = (Array.isArray(list) ? list : []).filter((t) => String(t?.type || '').toLowerCase() !== 'sequence');
      setCustomTypes(filtered);
      const ids = (filtered || []).map((t) => String(t?.id)).filter(Boolean);
      if (typeId && !ids.includes(String(typeId))) {
        setTypeId(ids[0] || '');
      }
      if (!typeId && ids.length) setTypeId(String(ids[0]));
    } catch (e) {
      setMsg({ type: 'error', text: e?.message || t('evaluationControlCenter.setupTypesManager.errors.loadCustomEvaluations') });
    } finally {
      setLoadingTypes(false);
    }
  }, [typeId]);

  useEffect(() => {
    loadCustomTypes();
  }, [loadCustomTypes]);

  useEffect(() => {
    const handler = () => loadCustomTypes();
    window.addEventListener('app:phase:changed', handler);
    window.addEventListener('app:ay:changed', handler);
    return () => {
      window.removeEventListener('app:phase:changed', handler);
      window.removeEventListener('app:ay:changed', handler);
    };
  }, [loadCustomTypes]);

  const handleAssign = async () => {
    if (!typeId) { setMsg({ type: 'error', text: t('evaluationControlCenter.setupTypesManager.errors.selectCustomEvaluation') }); return; }
    if (!classIds.length) { setMsg({ type: 'error', text: t('evaluationControlCenter.setupTypesManager.errors.selectAtLeastOneClass') }); return; }
    setSaving(true);
    try {
      const headers = { 'Content-Type': 'application/json' };
      const phaseId = localStorage.getItem('schoolAdmin_activePhaseId') || localStorage.getItem('activePhaseId') || '';
      const phaseCode = localStorage.getItem('schoolAdmin_activePhase') || localStorage.getItem('activePhase') || '';
      if (phaseId) headers['X-Active-Phase-Id'] = String(phaseId);
      if (phaseCode) headers['X-Active-Phase'] = String(phaseCode);

      await fetchJson(`/api/academics/evaluations/types/${encodeURIComponent(typeId)}/assign-classes`, {
        method: 'POST',
        headers,
        body: { classIds }
      });
      setMsg({ type: 'success', text: t('evaluationControlCenter.setupTypesManager.snack.assignedCustomEvaluation', { count: classIds.length }) });
    } catch (e) {
      setMsg({ type: 'error', text: e?.message || t('evaluationControlCenter.setupTypesManager.errors.assignFailed') });
    } finally {
      setSaving(false);
    }
  };

  const handleCreate = async () => {
    const name = String(createValues?.name || '').trim();
    if (!name) { setMsg({ type: 'error', text: t('evaluationControlCenter.setupTypesManager.errors.nameRequired') }); return; }
    setSaving(true);
    try {
      const phaseId = localStorage.getItem('schoolAdmin_activePhaseId') || localStorage.getItem('activePhaseId') || '';
      const phaseCode = localStorage.getItem('schoolAdmin_activePhase') || localStorage.getItem('activePhase') || '';
      const headers = { 'Content-Type': 'application/json' };
      if (phaseId) headers['X-Active-Phase-Id'] = String(phaseId);
      if (phaseCode) headers['X-Active-Phase'] = String(phaseCode);

      const row = await fetchJson('/api/evaluation-types', {
        method: 'POST',
        headers,
        body: {
          type: 'custom',
          name,
          state: (String(createValues?.state || 'closed').toLowerCase() === 'open' ? 'open' : 'closed')
        }
      });
      setCreateOpen(false);
      if (row?.id) setTypeId(String(row.id));
      setCreateValues({ name: '', state: 'closed' });
      setMsg({ type: 'success', text: t('evaluationControlCenter.setupTypesManager.snack.customEvaluationCreated') });
      loadCustomTypes();
    } catch (e) {
      setMsg({ type: 'error', text: e?.message || t('evaluationControlCenter.setupTypesManager.errors.createFailed') });
    } finally {
      setSaving(false);
    }
  };

  const openEditDialog = (evalType) => {
    setEditId(String(evalType.id));
    setEditValues({ name: evalType.name || '', state: evalType.state || 'closed' });
    setEditOpen(true);
  };

  const handleEdit = async () => {
    const name = String(editValues?.name || '').trim();
    if (!name) { setMsg({ type: 'error', text: t('evaluationControlCenter.setupTypesManager.errors.nameRequired') }); return; }
    setSaving(true);
    try {
      const phaseId = localStorage.getItem('schoolAdmin_activePhaseId') || localStorage.getItem('activePhaseId') || '';
      const phaseCode = localStorage.getItem('schoolAdmin_activePhase') || localStorage.getItem('activePhase') || '';
      const headers = { 'Content-Type': 'application/json' };
      if (phaseId) headers['X-Active-Phase-Id'] = String(phaseId);
      if (phaseCode) headers['X-Active-Phase'] = String(phaseCode);

      await fetchJson(`/api/evaluation-types/${encodeURIComponent(editId)}`, {
        method: 'PUT',
        headers,
        body: {
          name,
          state: (String(editValues?.state || 'closed').toLowerCase() === 'open' ? 'open' : 'closed')
        }
      });
      setEditOpen(false);
      setEditId(null);
      setEditValues({ name: '', state: 'closed' });
      setMsg({ type: 'success', text: t('evaluationControlCenter.setupTypesManager.snack.customEvaluationUpdated') });
      loadCustomTypes();
    } catch (e) {
      setMsg({ type: 'error', text: e?.message || t('evaluationControlCenter.setupTypesManager.errors.updateFailed') });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    setSaving(true);
    try {
      const phaseId = localStorage.getItem('schoolAdmin_activePhaseId') || localStorage.getItem('activePhaseId') || '';
      const phaseCode = localStorage.getItem('schoolAdmin_activePhase') || localStorage.getItem('activePhase') || '';
      const headers = {};
      if (phaseId) headers['X-Active-Phase-Id'] = String(phaseId);
      if (phaseCode) headers['X-Active-Phase'] = String(phaseCode);

      await fetchJson(`/api/evaluation-types/${encodeURIComponent(deleteId)}`, {
        method: 'DELETE',
        headers
      });
      setDeleteOpen(false);
      setDeleteId(null);
      if (String(typeId) === String(deleteId)) setTypeId('');
      setMsg({ type: 'success', text: t('evaluationControlCenter.setupTypesManager.snack.customEvaluationDeleted') });
      loadCustomTypes();
    } catch (e) {
      setMsg({ type: 'error', text: e?.message || t('evaluationControlCenter.setupTypesManager.errors.deleteFailed') });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card>
      <CardContent>
        <Typography variant="h6" gutterBottom>{t('evaluationControlCenter.setupTypesManager.customEvaluations')}</Typography>

        {/* List of existing custom evaluations with edit/delete actions */}
        {customTypes.length > 0 && (
          <TableContainer component={Paper} variant="outlined" sx={{ mb: 2, width: '100%', overflowX: 'auto' }}>
            {/* Three columns fit a phone; 480px did not. Same treatment as the
                marks table below — the min-width earns its keep on a laptop and
                only forces a sideways drag on a handset. */}
            <Table
              size="small"
              sx={{ minWidth: { xs: 0, sm: 480 }, tableLayout: { xs: 'fixed', sm: 'auto' } }}
            >
              <TableHead>
                <TableRow>
                  <TableCell sx={{ width: { xs: '44%', sm: 'auto' }, px: { xs: 0.75, sm: 2 } }}>
                    <strong>{t('evaluationControlCenter.setupTypesManager.name')}</strong>
                  </TableCell>
                  <TableCell sx={{ width: { xs: '28%', sm: 'auto' }, px: { xs: 0.75, sm: 2 } }}>
                    <strong>{t('evaluationControlCenter.setupTypesManager.state')}</strong>
                  </TableCell>
                  <TableCell align="right" sx={{ width: { xs: '28%', sm: 'auto' }, px: { xs: 0.75, sm: 2 } }}>
                    <strong>{t('evaluationControlCenter.common.actions')}</strong>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {customTypes.map((et) => (
                  <TableRow key={et.id} selected={String(typeId) === String(et.id)} hover sx={{ cursor: 'pointer' }} onClick={() => setTypeId(String(et.id))}>
                    <TableCell sx={{ px: { xs: 0.75, sm: 2 }, whiteSpace: 'normal', wordBreak: 'break-word' }}>
                      {et.name || t('evaluationControlCenter.setupTypesManager.customLabel', { n: et.number })}
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={et.state === 'open'
                          ? t('evaluationControlCenter.setupTypesManager.states.open')
                          : t('evaluationControlCenter.setupTypesManager.states.closed')}
                        color={et.state === 'open' ? 'success' : 'default'}
                      />
                    </TableCell>
                    <TableCell align="right">
                      <Button size="small" onClick={(e) => { e.stopPropagation(); openEditDialog(et); }}>
                        {t('evaluationControlCenter.common.edit')}
                      </Button>
                      <Button size="small" color="error" onClick={(e) => { e.stopPropagation(); setDeleteId(String(et.id)); setDeleteOpen(true); }}>
                        {t('evaluationControlCenter.common.delete')}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
        {customTypes.length === 0 && !loadingTypes && (
          <Alert severity="info" sx={{ mb: 2 }}>{t('evaluationControlCenter.setupTypesManager.noCustomEvaluations')}</Alert>
        )}

        <Divider sx={{ my: 2 }} />

        <Typography variant="subtitle2" gutterBottom>{t('evaluationControlCenter.setupTypesManager.assignSection')}</Typography>
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={12} md={8}>
            <FormControl fullWidth>
              <InputLabel id="assign-class-select">{t('evaluationControlCenter.setupTypesManager.classes')}</InputLabel>
              <Select
                labelId="assign-class-select"
                label={t('evaluationControlCenter.setupTypesManager.classes')}
                multiple
                value={classIds}
                onChange={(e) => setClassIds(typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value)}
                renderValue={(selected) => (selected || []).map(id => {
                  const c = phaseClasses.find(x => String(x.id) === String(id));
                  return c ? (c.name || c.className || t('evaluationControlCenter.common.classWithId', { id: c.id })) : String(id);
                }).join(', ')}
              >
                {phaseClasses.map(c => (
                  <MenuItem key={c.id} value={c.id}>
                    <Checkbox checked={classIds.map(String).includes(String(c.id))} />
                    {c.name || c.className || t('evaluationControlCenter.common.classWithId', { id: c.id })}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={4}>
            <FormControl fullWidth disabled={loadingTypes}>
              <InputLabel id="assign-type-select">{t('evaluationControlCenter.setupTypesManager.evaluation')}</InputLabel>
              <Select
                labelId="assign-type-select"
                label={t('evaluationControlCenter.setupTypesManager.evaluationType')}
                value={typeId}
                onChange={(e) => setTypeId(e.target.value)}
              >
                {(customTypes || []).map((type) => (
                  <MenuItem key={type.id} value={String(type.id)}>
                    {type?.name || `${String(type?.type || '').toLowerCase() === 'custom'
                      ? t('evaluationControlCenter.setupTypesManager.typeNames.custom')
                      : (type?.type || t('evaluationControlCenter.levelLabels.unknown'))} ${type?.number || ''}`.trim()}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>

        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {t('evaluationControlCenter.setupTypesManager.autoAssignNote')}
        </Typography>

        <Divider sx={{ my: 2 }} />

        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Button variant="outlined" disabled={saving} onClick={() => setCreateOpen(true)}>
            {t('evaluationControlCenter.setupTypesManager.addCustomEvaluation')}
          </Button>
          <Button variant="contained" disabled={!classIds.length || !typeId || saving} onClick={handleAssign}>
            {saving ? t('evaluationControlCenter.saving') : t('evaluationControlCenter.assignToSelectedClasses')}
          </Button>
        </Box>
      </CardContent>

      {/* Create Dialog */}
      <Dialog open={createOpen} onClose={() => setCreateOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{t('evaluationControlCenter.setupTypesManager.addCustomEvaluation')}</DialogTitle>
        <DialogContent>
          <TextField
            label={t('evaluationControlCenter.setupTypesManager.name')}
            fullWidth
            sx={{ mt: 1, mb: 2 }}
            value={createValues.name}
            onChange={(e) => setCreateValues((p) => ({ ...p, name: e.target.value }))}
          />
          <TextField
            select
            label={t('evaluationControlCenter.setupTypesManager.state')}
            fullWidth
            value={createValues.state}
            onChange={(e) => setCreateValues((p) => ({ ...p, state: e.target.value }))}
            SelectProps={{ native: true }}
          >
            <option value="closed">{t('evaluationControlCenter.setupTypesManager.states.closed')}</option>
            <option value="open">{t('evaluationControlCenter.setupTypesManager.states.open')}</option>
          </TextField>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateOpen(false)} disabled={saving}>{t('evaluationControlCenter.common.cancel')}</Button>
          <Button variant="contained" onClick={handleCreate} disabled={saving}>{t('evaluationControlCenter.common.create')}</Button>
        </DialogActions>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={editOpen} onClose={() => setEditOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{t('evaluationControlCenter.setupTypesManager.editCustomEvaluation')}</DialogTitle>
        <DialogContent>
          <TextField
            label={t('evaluationControlCenter.setupTypesManager.name')}
            fullWidth
            sx={{ mt: 1, mb: 2 }}
            value={editValues.name}
            onChange={(e) => setEditValues((p) => ({ ...p, name: e.target.value }))}
          />
          <TextField
            select
            label={t('evaluationControlCenter.setupTypesManager.state')}
            fullWidth
            value={editValues.state}
            onChange={(e) => setEditValues((p) => ({ ...p, state: e.target.value }))}
            SelectProps={{ native: true }}
          >
            <option value="closed">{t('evaluationControlCenter.setupTypesManager.states.closed')}</option>
            <option value="open">{t('evaluationControlCenter.setupTypesManager.states.open')}</option>
          </TextField>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditOpen(false)} disabled={saving}>{t('evaluationControlCenter.common.cancel')}</Button>
          <Button variant="contained" onClick={handleEdit} disabled={saving}>{t('evaluationControlCenter.save')}</Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteOpen} onClose={() => setDeleteOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>{t('evaluationControlCenter.setupTypesManager.deleteCustomEvaluation')}</DialogTitle>
        <DialogContent>
          <Typography>{t('evaluationControlCenter.setupTypesManager.deleteConfirmation')}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteOpen(false)} disabled={saving}>{t('evaluationControlCenter.common.cancel')}</Button>
          <Button variant="contained" color="error" onClick={handleDelete} disabled={saving}>{t('evaluationControlCenter.common.delete')}</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={!!msg} autoHideDuration={3000} onClose={() => setMsg(null)}>
        {msg && <Alert severity={msg.type} onClose={() => setMsg(null)}>{msg.text}</Alert>}
      </Snackbar>
    </Card>
  );
}

function MarkEntryAdmin() {
  const { t, i18n } = useTranslation();
  const { activePhase } = usePhase();
  const { classes: phaseClasses, loading: classesLoading } = usePhaseClasses({ allowEmptyFallback: false });
  const [sequences, setSequences] = useState([]);
  const [seqNo, setSeqNo] = useState('');
  const [level, setLevel] = useState('');
  const [classId, setClassId] = useState('');
  const [evaluations, setEvaluations] = useState([]);
  const [selectedEvaluationId, setSelectedEvaluationId] = useState('');
  const [subjects, setSubjects] = useState([]);
  const [subjectId, setSubjectId] = useState('');
  const [students, setStudents] = useState([]);
  const [rosterStudentIds, setRosterStudentIds] = useState([]);
  const [scores, setScores] = useState({});
  const [marksMap, setMarksMap] = useState({});
  const [evaluationId, setEvaluationId] = useState('');
  const [scoreErrors, setScoreErrors] = useState({});

  // THE SCHOOL'S OWN PASS MARK, not the default.
  //
  // `schools.passAverage` is set on this very page, a few cards above the mark
  // sheet, so colouring against a hard-coded 10 would have contradicted the
  // number the user had just typed. DEFAULT_PASS_MARK applies only until the
  // answer arrives, and if the school has never set one.
  // THE SCHOOL'S OWN PASS MARK, off the session rather than fetched.
  //
  // It lives on the school record, but the endpoint that serves it sits behind
  // page access for school settings, payments or finance — none of which an
  // ordinary teacher holds, so asking for it there would 403 for exactly the
  // people who spend most time on this screen, and the sheet would silently
  // colour against 10 while the school had set something else.
  //
  // /users/me carries it instead: loaded once at startup, school-scoped, and
  // already in context here. DEFAULT_PASS_MARK is the fallback for a school
  // that has never set one.
  const sessionUser = useContext(UserContext);
  const passMark = useMemo(() => {
    const v = Number(sessionUser?.school?.passAverage);
    return Number.isFinite(v) && v > 0 ? v : DEFAULT_PASS_MARK;
  }, [sessionUser]);

  /**
   * Green at or above the pass mark, red below, and nothing when empty.
   *
   * Text colour and weight only — the outline belongs to `error`, which means
   * "this is not a valid mark" and must not be drowned out by a verdict on a
   * number that was never accepted in the first place.
   */
  const markSx = useCallback((value) => {
    const verdict = isPass(value, passMark);
    if (verdict === null) return undefined;
    return {
      '& input': {
        color: verdict ? 'success.main' : 'error.main',
        fontWeight: 700,
      },
    };
  }, [passMark]);
  const [ratings, setRatings] = useState({});
  const [ratingErrors, setRatingErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);

  const selectedClassObj = useMemo(
    () => (phaseClasses || []).find((c) => String(c.id) === String(classId)) || null,
    [phaseClasses, classId]
  );

  const nurseryRubricScale = useMemo(() => getNurseryRubricScale(t), [i18n.language, t]);

  const resolveGradeLabelForMark = useCallback((mark, scales) => {
    const n = Number(mark);
    if (!Number.isFinite(n) || !Array.isArray(scales) || scales.length === 0) return '';
    const hit = scales.find((s) => {
      const min = Number(s?.minScore);
      const max = Number(s?.maxScore);
      if (!Number.isFinite(min) || !Number.isFinite(max)) return false;
      return n >= min && n <= max;
    });
    if (!hit) return '';
    return String(hit.grade || hit.remarkCode || hit.name || '').trim();
  }, []);

  const levelOptions = useMemo(() => {
    const resolvedPhaseCode = (activePhase || localStorage.getItem('schoolAdmin_activePhase') || localStorage.getItem('activePhase') || '')
      .toString()
      .trim()
      .toLowerCase();
    if (resolvedPhaseCode === 'sec') return ['first_cycle', 'second_cycle'];
    if (resolvedPhaseCode === 'nur_pri') return ['nursery', 'primary'];
    const set = new Set((phaseClasses || []).map((c) => (c?.level || '').toString()).filter(Boolean));
    return Array.from(set);
  }, [activePhase, phaseClasses]);

  useEffect(() => {
    if (level && !levelOptions.includes(level)) setLevel('');
  }, [level, levelOptions]);

  const filteredClasses = useMemo(() => {
    if (!level) return phaseClasses || [];
    return (phaseClasses || []).filter((c) => String(c?.level || '') === String(level));
  }, [phaseClasses, level]);

  const selectionRequiredForClass = useMemo(() => {
    const level = (selectedClassObj?.level || '').toString();
    const isSecondary = level === 'first_cycle' || level === 'second_cycle';
    const isSecondCycle = level === 'second_cycle';
    const isExaminationClass = !!selectedClassObj?.isExaminationClass;
    return isSecondary && (isSecondCycle || isExaminationClass);
  }, [selectedClassObj]);

  const isNurseryMode = useMemo(() => {
    const level = (selectedClassObj?.level || '').toString();
    return level === 'nursery';
  }, [selectedClassObj]);

  const rosterSet = useMemo(() => {
    try {
      return new Set((rosterStudentIds || []).map((id) => String(id)));
    } catch (_) {
      return new Set();
    }
  }, [rosterStudentIds]);

  const loadSequences = useCallback(async () => {
    try {
      const phaseId = localStorage.getItem('schoolAdmin_activePhaseId') || localStorage.getItem('activePhaseId') || '';
      const headers = {};
      if (phaseId) headers['X-Active-Phase-Id'] = String(phaseId);
      const data = await fetchJson('/api/academics/evaluations/setup/types', { headers });
      const list = (data?.types || []).filter(t => t.type === 'sequence').map(t => ({ sequenceNo: t.number, status: t.state }));
      setSequences(list);
      if (!seqNo && list.length) setSeqNo(String(list[0].sequenceNo));
    } catch {}
  }, [seqNo]);

  useEffect(() => { loadSequences(); }, [loadSequences]);

  useEffect(() => {
    const handler = () => {
      loadSequences();
      setLevel('');
      setClassId('');
      setEvaluations([]);
      setSelectedEvaluationId('');
      setSubjects([]);
      setSubjectId('');
      setStudents([]);
      setRosterStudentIds([]);
      setScores({});
      setMarksMap({});
      setEvaluationId('');
      setScoreErrors({});
      setRatings({});
      setRatingErrors({});
    };
    window.addEventListener('app:phase:changed', handler);
    window.addEventListener('app:ay:changed', handler);
    return () => {
      window.removeEventListener('app:phase:changed', handler);
      window.removeEventListener('app:ay:changed', handler);
    };
  }, [loadSequences]);

  // Clear class selection if it disappears due to phase change
  useEffect(() => {
    if (classId && !phaseClasses.find(c => String(c.id) === String(classId))) {
      setClassId('');
      setEvaluations([]);
      setSelectedEvaluationId('');
      setSubjects([]);
      setSubjectId('');
    }
  }, [phaseClasses, classId]);

  // Load class-linked evaluations for Mark Entry (control-center permission)
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!classId) {
        if (!cancelled) {
          setEvaluations([]);
          setSelectedEvaluationId('');
          setSeqNo('');
          setEvaluationId('');
        }
        return;
      }

      try {
        const schoolId = localStorage.getItem('activeSchoolId') || localStorage.getItem('scopeSchoolId') || localStorage.getItem('schoolId') || '';
        const phaseCode = localStorage.getItem('schoolAdmin_activePhase') || localStorage.getItem('activePhase') || '';
        const ayKey = schoolId ? `${schoolId}:currentAcademicYearId` : 'currentAcademicYearId';
        const ayId = localStorage.getItem(ayKey) || '';
        const phaseId = localStorage.getItem('schoolAdmin_activePhaseId') || localStorage.getItem('activePhaseId') || '';
        const headers = {
          ...(schoolId ? { 'X-Scope-School-Id': schoolId } : {}),
          ...(phaseCode ? { 'X-Active-Phase': phaseCode } : {}),
          ...(ayId ? { 'X-Academic-Year-Id': ayId } : {}),
          ...(phaseId ? { 'X-Active-Phase-Id': phaseId } : {}),
        };

        const r = await fetchJson(`/api/academics/evaluations/class/${encodeURIComponent(classId)}/evaluations/control-center`, { headers });
        const list = Array.isArray(r) ? r : (r?.evaluations || []);
        const usable = (list || []).filter((ev) => (ev?.type === 'sequence' && ev?.sequenceNo) || ev?.type === 'mock');

        if (!cancelled) {
          setEvaluations(usable);
          setSelectedEvaluationId((prev) => {
            if (prev && usable.some((ev) => String(ev.id) === String(prev))) return String(prev);
            return usable[0]?.id ? String(usable[0].id) : '';
          });
        }
      } catch (_) {
        if (!cancelled) {
          setEvaluations([]);
          setSelectedEvaluationId('');
          setSeqNo('');
          setEvaluationId('');
        }
      }
    })();

    return () => { cancelled = true; };
  }, [classId]);

  useEffect(() => {
    const picked = (evaluations || []).find((ev) => String(ev.id) === String(selectedEvaluationId)) || null;
    setEvaluationId(picked?.id ? String(picked.id) : '');
    setSeqNo(picked?.sequenceNo ? String(picked.sequenceNo) : '');
  }, [evaluations, selectedEvaluationId]);

  // No manual class loading here; usePhaseClasses handles it.

  useEffect(() => {
    (async () => {
      if (!classId) { setSubjects([]); setSubjectId(''); return; }
      try {
        const schoolId = localStorage.getItem('activeSchoolId') || localStorage.getItem('scopeSchoolId') || localStorage.getItem('schoolId') || '';
        const phaseCode = localStorage.getItem('schoolAdmin_activePhase') || localStorage.getItem('activePhase') || '';
        const ayKey = schoolId ? `${schoolId}:currentAcademicYearId` : 'currentAcademicYearId';
        const ayId = localStorage.getItem(ayKey) || '';
        const phaseId = localStorage.getItem('schoolAdmin_activePhaseId') || localStorage.getItem('activePhaseId') || '';
        const headers = schoolId
          ? { 'X-Scope-School-Id': schoolId, 'X-Active-Phase': phaseCode, 'X-Academic-Year-Id': ayId, 'X-Active-Phase-Id': phaseId }
          : { 'X-Active-Phase': phaseCode, 'X-Academic-Year-Id': ayId, 'X-Active-Phase-Id': phaseId };

        const q = `?academicYearId=${encodeURIComponent(ayId)}&phase=${encodeURIComponent(phaseCode)}&schoolId=${encodeURIComponent(schoolId)}`;

        const offered = await fetchJson(`/api/class-subjects/class/${encodeURIComponent(classId)}${q}`, { headers }).catch(() => []);
        let subsRaw = Array.isArray(offered) ? offered : (offered?.classSubjects || offered?.data || []);
        if (!Array.isArray(subsRaw)) subsRaw = [];

        setSubjects(subsRaw);
        setSubjectId('');
      } catch {}
    })();
  }, [classId]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!classId || !subjectId) { setStudents([]); setRosterStudentIds([]); setScores({}); setMarksMap({}); setEvaluationId(''); setScoreErrors({}); setRatings({}); setRatingErrors({}); return; }
      setLoading(true);
      try {
        const schoolId = localStorage.getItem('activeSchoolId') || localStorage.getItem('scopeSchoolId') || localStorage.getItem('schoolId') || '';
        const phaseCode = localStorage.getItem('schoolAdmin_activePhase') || localStorage.getItem('activePhase') || '';
        const ayKey = schoolId ? `${schoolId}:currentAcademicYearId` : 'currentAcademicYearId';
        const ayId = localStorage.getItem(ayKey) || '';
        const phaseId = localStorage.getItem('schoolAdmin_activePhaseId') || localStorage.getItem('activePhaseId') || '';
        const ctxHeaders = schoolId
          ? { 'X-Scope-School-Id': schoolId, 'X-Active-Phase': phaseCode, 'X-Academic-Year-Id': ayId, 'X-Active-Phase-Id': phaseId }
          : { 'X-Active-Phase': phaseCode, 'X-Academic-Year-Id': ayId, 'X-Active-Phase-Id': phaseId };

        const resolvedEvalId = selectedEvaluationId ? String(selectedEvaluationId) : '';
        if (!cancelled) setEvaluationId(resolvedEvalId);

        const roster = await fetchJson(`/api/allocations/roster/${encodeURIComponent(classId)}/${encodeURIComponent(subjectId)}`, { headers: ctxHeaders }).catch(() => ({ students: [] }));
        const rosterStudents = Array.isArray(roster?.students) ? roster.students : [];
        if (!cancelled) {
          setRosterStudentIds((rosterStudents || []).map((s) => s?.id).filter(Boolean));
        }
        const all = await fetchJson(`/api/allocations/students/class/${encodeURIComponent(classId)}`, { headers: ctxHeaders }).catch(() => ({ students: [] }));
        const studentData = Array.isArray(all?.students) ? all.students : [];

        let existingMap = {};
        try {
          const evalIdToUse = resolvedEvalId;
          if (evalIdToUse && subjectId) {
            const baseEndpoint = isNurseryMode ? '/api/nursery-marks/control-center' : '/api/marks/control-center';
            const markData = await fetchJson(`${baseEndpoint}?classId=${encodeURIComponent(classId)}&subjectId=${encodeURIComponent(subjectId)}&evaluationId=${encodeURIComponent(evalIdToUse)}&sequenceNo=${encodeURIComponent(seqNo)}`, { headers: ctxHeaders }).catch(() => []);
            for (const m of (Array.isArray(markData) ? markData : [])) {
              const sid = m?.Enrollment?.studentId ?? m?.Enrollment?.Student?.id ?? m?.studentId;
              if (!sid) continue;
              const val = isNurseryMode ? (m && (m.rating ?? null)) : ((m && (m.score ?? m.mark)) ?? null);
              existingMap[String(sid)] = isNurseryMode ? { ...m, rating: val } : { ...m, score: val };
            }
          }
        } catch (_) {
          existingMap = {};
        }

        const initial = {};
        const initialRatings = {};
        (studentData || []).forEach(s => {
          if (isNurseryMode) {
            const existing = existingMap[String(s.id)]?.rating;
            const n = Number(existing);
            initialRatings[s.id] = Number.isFinite(n) ? String(Math.trunc(n)) : '';
          } else {
            const existing = existingMap[String(s.id)]?.score;
            const n = Number(existing);
            initial[s.id] = Number.isFinite(n) ? n.toFixed(1) : '';
          }
        });
        if (!cancelled) {
          setStudents(studentData || []);
          setMarksMap(existingMap);
          setScores(initial);
          setScoreErrors({});
          setRatings(initialRatings);
          setRatingErrors({});
        }
      } catch (e) {
        setMsg({ type: 'error', text: e?.message || 'Failed to load students' });
      } finally {
        setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [classId, subjectId, selectionRequiredForClass, seqNo, isNurseryMode, selectedEvaluationId]);

  const statusForSeq = useMemo(() => {
    const s = sequences.find(x => String(x.sequenceNo) === String(seqNo));
    return s?.status || 'closed';
  }, [sequences, seqNo]);

  const handleScoreChange = (studentId, value) => {
    setScores(prev => ({ ...prev, [studentId]: value }));
    setScoreErrors(prev => {
      if (!prev || !prev[studentId]) return prev;
      const next = { ...prev };
      delete next[studentId];
      return next;
    });
  };

  const normalizeScore = (raw) => {
    if (raw === '' || raw === null || raw === undefined) return null;
    const n = Number(raw);
    return n;
  };

  const clampAndFormatScore = (raw) => {
    const n = normalizeScore(raw);
    if (n === null) return '';
    if (!Number.isFinite(n)) return null;
    const clamped = Math.max(0, Math.min(20, n));
    return clamped.toFixed(1);
  };

  const handleScoreBlur = (studentId) => {
    setScores(prev => {
      const raw = prev[studentId];
      if (raw === '' || raw === null || raw === undefined) return { ...prev, [studentId]: '' };
      const formatted = clampAndFormatScore(raw);
      return { ...prev, [studentId]: (formatted ?? '') };
    });
  };

  const handleRatingSelect = (studentId, value) => {
    setRatings(prev => ({ ...prev, [studentId]: String(value) }));
    setRatingErrors(prev => {
      if (!prev || !prev[studentId]) return prev;
      const next = { ...prev };
      delete next[studentId];
      return next;
    });
  };


  const onSave = useCallback(async () => {
    setSaving(true);
    try {
      const nextErrors = {};
      const restrictToRoster = selectionRequiredForClass && rosterSet.size > 0;
      const eligibleStudents = restrictToRoster
        ? (students || []).filter((s) => rosterSet.has(String(s.id)))
        : (students || []);
      if (isNurseryMode) {
        for (const s of eligibleStudents) {
          const n = Number(ratings[s.id]);
          if (!Number.isFinite(n) || !Number.isInteger(n) || n < 1 || n > 5) nextErrors[s.id] = t('evaluationControlCenter.markEntry.errors.selectRating');
        }
        setRatingErrors(nextErrors);
      } else {
        for (const s of eligibleStudents) {
          const n = normalizeScore(scores[s.id]);
          if (n === null || !Number.isFinite(n)) nextErrors[s.id] = t('evaluationControlCenter.markEntry.errors.invalidScore');
          else if (n < 0 || n > 20) nextErrors[s.id] = t('evaluationControlCenter.markEntry.errors.scoreOutOfRange');
        }
        setScoreErrors(nextErrors);
      }
      if (Object.keys(nextErrors).length > 0) {
        setMsg({
          type: 'error',
          text: isNurseryMode
            ? t('evaluationControlCenter.markEntry.errors.fixInvalidRatingsBeforeSaving')
            : t('evaluationControlCenter.markEntry.errors.fixInvalidScoresBeforeSaving')
        });
        setSaving(false);
        return;
      }

      const marks = isNurseryMode
        ? eligibleStudents.map(s => ({ studentId: s.id, rating: Number(ratings[s.id]) }))
        : eligibleStudents.map(s => ({ studentId: s.id, score: Number(normalizeScore(scores[s.id]).toFixed(1)) }));
      const schoolId = localStorage.getItem('activeSchoolId') || localStorage.getItem('scopeSchoolId') || localStorage.getItem('schoolId') || '';
      const phaseCode = localStorage.getItem('schoolAdmin_activePhase') || localStorage.getItem('activePhase') || '';
      const ayKey = schoolId ? `${schoolId}:currentAcademicYearId` : 'currentAcademicYearId';
      const ayId = localStorage.getItem(ayKey) || '';
      const phaseId = localStorage.getItem('schoolAdmin_activePhaseId') || localStorage.getItem('activePhaseId') || '';
      const headers = {
        'Content-Type': 'application/json',
        ...(schoolId ? { 'X-Scope-School-Id': schoolId } : {}),
        'X-Active-Phase': phaseCode,
        'X-Academic-Year-Id': ayId,
        'X-Active-Phase-Id': phaseId
      };

      if (isNurseryMode) {
        await fetchJson('/api/nursery-marks/bulk/control-center', {
          method: 'POST',
          headers,
          body: { classId, subjectId, evaluationId, marks }
        });
      } else {
        await fetchJson(`/api/academics/evaluations/sequence/${encodeURIComponent(seqNo)}/marks/admin-bulk`, {
          method: 'POST',
          headers,
          body: { classId, subjectId, marks }
        });
      }
      setMsg({ type: 'success', text: t('evaluationControlCenter.markEntry.snack.saved') });
    } catch (e) {
      const serverMsg = e?.data?.error || e?.data?.message || e?.message || t('evaluationControlCenter.markEntry.errors.saveFail');
      setMsg({ type: 'error', text: serverMsg });
    } finally {
      setSaving(false);
    }
  }, [seqNo, classId, subjectId, students, scores, isNurseryMode, ratings, evaluationId, selectionRequiredForClass, rosterSet, t]);

  const hasExistingMarks = useMemo(() => {
    try {
      const ids = (students || []).map(s => String(s.id));
      return ids.some((id) => !!marksMap?.[id]);
    } catch (_) {
      return false;
    }
  }, [students, marksMap]);

  return (
    <Card>
      <CardContent>
        <Typography variant="h6" gutterBottom>{t('evaluationControlCenter.markEntry.title')}</Typography>
        <Grid container spacing={2} sx={{ mb: 1 }}>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth>
              <InputLabel id="level-select-label">{t('evaluationControlCenter.common.level')}</InputLabel>
              <Select
                labelId="level-select-label"
                label={t('evaluationControlCenter.common.level')}
                value={level}
                onChange={(e) => {
                  setLevel(e.target.value);
                  setClassId('');
                  setSelectedEvaluationId('');
                  setSubjects([]);
                  setSubjectId('');
                }}
              >
                <MenuItem value=""><em>{t('evaluationControlCenter.common.allLevels')}</em></MenuItem>
                {levelOptions.map((lvl) => (
                  <MenuItem key={lvl} value={lvl}>{levelLabel(t, lvl)}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth>
              <InputLabel id="class-select-label">{t('evaluationControlCenter.common.class')}</InputLabel>
              <Select labelId="class-select-label" label={t('evaluationControlCenter.common.class')} value={classId} onChange={(e) => { setClassId(e.target.value); setSelectedEvaluationId(''); setSubjectId(''); }}>
                <MenuItem value=""><em>{t('evaluationControlCenter.common.selectClass')}</em></MenuItem>
                {filteredClasses.map(c => <MenuItem key={c.id} value={c.id}>{c.name || c.className || t('evaluationControlCenter.common.classWithId', { id: c.id })}</MenuItem>)}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth disabled={!classId}>
              <InputLabel id="eval-select-label">{t('evaluationControlCenter.common.evaluation', 'Evaluation')}</InputLabel>
              <Select
                labelId="eval-select-label"
                label={t('evaluationControlCenter.common.evaluation', 'Evaluation')}
                value={selectedEvaluationId}
                onChange={(e) => setSelectedEvaluationId(e.target.value)}
              >
                <MenuItem value=""><em>{t('evaluationControlCenter.common.selectEvaluation', 'Select evaluation')}</em></MenuItem>
                {(evaluations || []).map((ev) => (
                  <MenuItem key={ev.id} value={String(ev.id)}>
                    {evaluationOptionLabel(t, ev) || (ev.type || String(ev.id))}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth disabled={!classId}>
              <InputLabel id="subject-select-label">{t('evaluationControlCenter.common.subject')}</InputLabel>
              <Select
                labelId="subject-select-label"
                label={t('evaluationControlCenter.common.subject')}
                value={subjectId}
                onChange={(e) => setSubjectId(e.target.value)}
              >
                <MenuItem value=""><em>{t('evaluationControlCenter.common.selectSubject')}</em></MenuItem>
                {subjects.map((cs) => {
                  const subj = cs.Subject || cs.subject;
                  const sid = subj?.id || cs.subjectId || '';
                  const label = subj?.name || (sid ? t('evaluationControlCenter.common.subjectWithId', { id: sid }) : t('evaluationControlCenter.common.subject'));
                  return (
                    <MenuItem key={cs.id} value={sid}>{label}</MenuItem>
                  );
                })}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={3} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Chip
              size="small"
              label={t('evaluationControlCenter.markEntry.window', { state: t(`evaluationControlCenter.setupTypesManager.states.${statusForSeq === 'open' ? 'open' : 'closed'}`) })}
              color={statusForSeq === 'open' ? 'success' : 'default'}
            />
          </Grid>
        </Grid>

        {loading ? (
          <Box sx={{ py: 2 }}>
            <AppLoader
              variant="circular3"
              size={56}
              messages={[
                t('evaluationControlCenter.markEntry.loader.loadingStudents'),
                t('evaluationControlCenter.markEntry.loader.applyingFilters'),
                t('evaluationControlCenter.markEntry.loader.applyingPhaseAy')
              ]}
            />
          </Box>
        ) : (
          <>
            {classId && subjectId && !evaluationId && (
              <Alert severity="info" sx={{ mb: 2 }}>
                {t('evaluationControlCenter.noEvaluationConfigured')}
              </Alert>
            )}
            {selectionRequiredForClass && rosterSet.size > 0 && rosterSet.size < students.length && (
              <Alert severity="info" sx={{ mb: 2 }}>
                {t('evaluationControlCenter.someStudentsNotRegistered')}
              </Alert>
            )}
            {students.length === 0 && classId && subjectId && (
              <Alert severity="info">{t('evaluationControlCenter.noStudentsFound')}</Alert>
            )}
            {students.length > 0 && (() => {
              const total = students.length;
              const enteredCount = students.filter(s => !!marksMap[String(s.id)]).length;
              const pendingCount = total - enteredCount;
              const errorCount = Object.keys(isNurseryMode ? ratingErrors : scoreErrors).length;
              const recorders = new Map();
              for (const s of students) {
                const existing = marksMap[String(s.id)];
                if (!existing) continue;
                const name = existing?.Recorder?.name || t('evaluationControlCenter.common.unknown');
                const whenRaw = existing?.updatedAt || existing?.createdAt || null;
                const when = whenRaw ? new Date(whenRaw) : null;
                const key = name;
                if (!recorders.has(key)) recorders.set(key, { name, when, count: 0 });
                recorders.get(key).count++;
              }
              const recorderEntries = [...recorders.values()];
              return (
                <Alert severity={errorCount > 0 ? 'warning' : 'info'} sx={{ mb: 1 }}>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center', mb: recorderEntries.length || errorCount ? 0.5 : 0 }}>
                    <Typography variant="body2" component="span"><strong>{t('evaluationControlCenter.markEntry.banner.totalStudents', { count: total })}</strong></Typography>
                    <Typography variant="body2" component="span" color="success.main">{t('evaluationControlCenter.markEntry.banner.entered', { count: enteredCount })}</Typography>
                    {pendingCount > 0 && <Typography variant="body2" component="span" color="text.secondary">{t('evaluationControlCenter.markEntry.banner.pending', { count: pendingCount })}</Typography>}
                    {errorCount > 0 && <Typography variant="body2" component="span" color="error.main">{t('evaluationControlCenter.markEntry.banner.errors', { count: errorCount })}</Typography>}
                  </Box>
                  {recorderEntries.map((e, i) => {
                    const whenText = e.when && !Number.isNaN(e.when.getTime()) ? e.when.toLocaleString() : '';
                    return (
                      <Typography key={i} variant="body2" component="div">
                        {t('evaluationControlCenter.markEntry.banner.recordedBy', { count: e.count, name: e.name })}
                        {whenText ? ` — ${whenText}` : ''}
                      </Typography>
                    );
                  })}
                </Alert>
              );
            })()}
            {/* WHY A BLANK IS NOT A ZERO, said where the marks are typed.

                A missing mark is SKIPPED by every average in this product:
                sequence to term and term to annual both drop what is absent and
                divide by what is there. So a child who sat nothing is averaged
                on nothing and comes out looking like a child who sat
                everything, and a subject left entirely blank disappears from
                the report card rather than showing a gap.

                Nothing here writes a zero on anybody's behalf. A 0 counts
                against a child, and only the teacher knows whether an absence
                deserves it. The screen explains what a blank means and leaves
                the judgement where it belongs. */}
            {students.length > 0 && !isNurseryMode && (
              <Alert severity="info" sx={{ mt: 1 }}>
                {t('evaluationControlCenter.zeroTip', {
                  defaultValue: 'Enter 0 for any student who was not evaluated. A blank is skipped by the averages rather than counted as nothing, so leaving it empty quietly drops the subject from that student\'s report card.',
                })}
              </Alert>
            )}
            {students.length > 0 && (
              <TableContainer component={Paper} variant="outlined" sx={{ mt: 1, width: '100%', overflowX: 'auto' }}>
                {/* NAME AND SCORE ON ONE SCREEN. NO SIDEWAYS DRAG.
                    A flat `minWidth: 560` (760 for the rubric) is wider than a
                    360px phone, so entering a mark meant dragging right to reach
                    the field, then dragging back to read who it belonged to —
                    for every child in the class. On a laptop the min-width does
                    something useful, so it stays there and only there.
                    A FIXED layout with percentage columns is what makes it fit:
                    without it the browser sizes columns to their content and the
                    student names alone push the score field off the edge. The
                    phone app has always laid this out as name-plus-field on one
                    line; this is the same thing said in a table. */}
                <Table
                  size="small"
                  sx={{
                    minWidth: { xs: 0, sm: isNurseryMode ? 760 : 560 },
                    tableLayout: { xs: 'fixed', sm: 'auto' },
                  }}
                >
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{
                        // The rubric needs five columns beside the name, so the
                        // name gives up more of the row there than it does when
                        // it only shares with a single score field.
                        // The name now carries a face as well as text, and the
                        // score beside it is at most four characters — "12.5".
                        // Giving the field 180px to hold that was taking width
                        // from the only column anybody reads.
                        width: { xs: isNurseryMode ? '40%' : '70%', sm: 'auto' },
                        px: { xs: 0.75, sm: 2 },
                      }}>
                        <strong>{t('evaluationControlCenter.student')}</strong>
                      </TableCell>
                      {isNurseryMode ? (
                        nurseryRubricScale.map((g) => (
                          <TableCell key={g.value} align="center" sx={{ backgroundColor: g.bg, py: 1.25, width: { xs: '12%', sm: 'auto' }, px: { xs: 0.25, sm: 2 } }}>
                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', lineHeight: 1.1, gap: 0.5 }}>
                              {renderNurseryRubricBadge(g)}
                              <Typography variant="caption" sx={{ color: g.text, fontWeight: 700, display: 'block', textAlign: 'center', lineHeight: 1.1 }}>{g.value}</Typography>
                            </Box>
                          </TableCell>
                        ))
                      ) : (
                        <TableCell sx={{ width: { xs: '30%', sm: 110 }, px: { xs: 0.75, sm: 1 } }}>
                          <strong>{t('evaluationControlCenter.score')}</strong>
                        </TableCell>
                      )}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {students.map((s) => {
                      const disabledRow = selectionRequiredForClass && rosterSet.size > 0 && !rosterSet.has(String(s.id));
                      return (
                        <TableRow key={s.id}>
                          <TableCell sx={{
                            px: { xs: 0.75, sm: 2 },
                            whiteSpace: 'normal',
                            wordBreak: 'break-word',
                            ...((ratingErrors[s.id] || scoreErrors[s.id])
                              ? { borderLeft: '3px solid', borderLeftColor: 'error.main' }
                              : {}),
                          }}>
                            {/* A FACE BESIDE THE NAME. A teacher entering thirty
                                marks is matching each row to a child they know
                                by sight, and a column of names alone makes that
                                a reading exercise. The photo where the school
                                has added one, a boy or a girl where it has not.
                                28px: big enough to recognise, small enough that
                                the name keeps the width it had — this column is
                                already tight on a phone. */}
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.75, sm: 1.25 }, minWidth: 0 }}>
                              <StudentAvatar student={s} size={28} sx={{ flexShrink: 0 }} />
                              <Box component="span" sx={{ minWidth: 0 }}>
                                {`${s.firstName || ''} ${s.lastName || ''}`.trim() || t('evaluationControlCenter.common.studentWithId', { id: s.id })}
                              </Box>
                            </Box>
                          </TableCell>
                          {isNurseryMode ? (
                            nurseryRubricScale.map((g) => {
                              const current = Number(ratings[s.id]);
                              const selected = Number.isFinite(current) && current === g.value;
                              return (
                                <TableCell
                                  key={g.value}
                                  align="center"
                                  onClick={() => { if (!disabledRow) handleRatingSelect(s.id, g.value); }}
                                  sx={{
                                    px: { xs: 0.25, sm: 2 },
                                    cursor: disabledRow ? 'not-allowed' : 'pointer',
                                    backgroundColor: selected ? g.bd : g.bg,
                                    border: selected ? `2px solid ${g.bd}` : '1px solid rgba(0,0,0,0.08)',
                                    color: selected ? '#fff' : g.text,
                                    fontWeight: 800,
                                    userSelect: 'none'
                                  }}
                                >
                                  {selected ? '✓' : ''}
                                </TableCell>
                              );
                            })
                          ) : (
                            <TableCell sx={{ px: { xs: 0.75, sm: 1 } }}>
                              <TextField
                                fullWidth
                                value={scores[s.id] ?? ''}
                                onChange={(e) => handleScoreChange(s.id, e.target.value)}
                                onBlur={() => handleScoreBlur(s.id)}
                                type="number"
                                size="small"
                                inputProps={{ min: 0, max: 20, step: 0.1 }}
                                disabled={disabledRow}
                                error={!!scoreErrors[s.id]}
                                // PASS IN GREEN, FAIL IN RED, AS IT IS TYPED.
                                //
                                // A teacher entering thirty marks is looking for
                                // the failures, and finding them meant reading
                                // every number against a threshold held in their
                                // head. The colour does that comparison for them.
                                //
                                // Weight as well as hue: colour alone is not a
                                // signal for a red-green colourblind reader, and
                                // this is exactly the pair they cannot separate.
                                //
                                // `isPass` returns null for an empty or
                                // unparseable field — not a failure, just not
                                // answered yet — so a blank row stays neutral
                                // rather than turning red the moment it is seen.
                                sx={markSx(scores[s.id])}
                              />
                            </TableCell>
                          )}
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </>
        )}

        <Box sx={{ mt: 2 }}>
          <Button variant="contained" disabled={!seqNo || !classId || !subjectId || !evaluationId || students.length === 0 || statusForSeq !== 'open' || saving} onClick={onSave}>
            {saving ? t('evaluationControlCenter.saving') : (hasExistingMarks ? t('evaluationControlCenter.editMarks') : t('evaluationControlCenter.saveMarks'))}
          </Button>
          {statusForSeq !== 'open' && (
            <Typography variant="caption" color="text.secondary" sx={{ ml: 2 }}>{t('evaluationControlCenter.windowClosed')}</Typography>
          )}
        </Box>
      </CardContent>
      <Snackbar open={!!msg} autoHideDuration={3000} onClose={() => setMsg(null)}>
        {msg && <Alert severity={msg.type} onClose={() => setMsg(null)}>{msg.text}</Alert>}
      </Snackbar>
    </Card>
  );
}

function ResultsViewer() {
  const { t, i18n } = useTranslation();
  const { activePhase } = usePhase();
  const { classes: phaseClasses } = usePhaseClasses({ allowEmptyFallback: false });
  const [seqNo, setSeqNo] = useState('');
  const [level, setLevel] = useState('');
  const [classId, setClassId] = useState('');
  const [evaluations, setEvaluations] = useState([]);
  const [selectedEvaluationId, setSelectedEvaluationId] = useState('');
  const [subjects, setSubjects] = useState([]);
  const [subjectId, setSubjectId] = useState('');
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState(null);

  const [meta, setMeta] = useState({});
  const [resultRows, setResultRows] = useState([]);
  const [noRecordedMarks, setNoRecordedMarks] = useState(false);
  const [passAvg, setPassAvg] = useState(null);
  const [gradeScales, setGradeScales] = useState([]);
  const [downloading, setDownloading] = useState(null);

  const buildAuthHeaders = useCallback(() => {
    const tokenKeys = ['impersonationToken', 'accessToken', 'token', 'authToken', 'jwt', 'id_token', 'access_token'];
    let token = null;
    for (const k of tokenKeys) {
      const v = localStorage.getItem(k) || sessionStorage.getItem(k);
      if (v) { token = v; break; }
    }
    const headers = {};
    if (token) headers['Authorization'] = `Bearer ${token}`;
    const phase = localStorage.getItem('schoolAdmin_activePhase') || localStorage.getItem('activePhase') || '';
    const phaseId = localStorage.getItem('schoolAdmin_activePhaseId') || localStorage.getItem('activePhaseId') || '';
    const schoolId = localStorage.getItem('activeSchoolId') || localStorage.getItem('scopeSchoolId') || localStorage.getItem('schoolId') || '';
    const ayId = localStorage.getItem(schoolId ? `${schoolId}:currentAcademicYearId` : 'currentAcademicYearId') || '';
    if (phase) headers['X-Active-Phase'] = phase;
    if (phaseId) headers['X-Active-Phase-Id'] = phaseId;
    if (schoolId) headers['X-Scope-School-Id'] = schoolId;
    if (ayId) headers['X-Academic-Year-Id'] = ayId;
    return headers;
  }, []);

  const buildApiUrl = useCallback((path) => {
    const configuredBaseRaw = (
      process.env.REACT_APP_API_BASE_URL
      || process.env.VITE_API_BASE_URL
      || process.env.NEXT_PUBLIC_API_BASE_URL
      || ''
    ).trim();
    const browserHost = typeof window !== 'undefined' ? window.location.hostname : '';
    const isLocalBrowserHost = ['localhost', '127.0.0.1', '0.0.0.0'].includes(browserHost);
    const isLocalNodeEnv = process.env.NODE_ENV === 'development';
    const baseRaw = configuredBaseRaw || ((isLocalBrowserHost || isLocalNodeEnv) ? 'http://localhost:5000' : '');
    const base = String(baseRaw || '').replace(/\/$/, '');
    if (/^https?:\/\//i.test(path)) return path;
    let pathPart = path.startsWith('/') ? path : `/${path}`;
    if (/\/api$/i.test(base) && /^\/api(\b|\/)/i.test(pathPart)) {
      pathPart = pathPart.replace(/^\/api/i, '');
    }
    return `${base}${pathPart}`;
  }, []);

  const downloadResults = useCallback(async (format) => {
    if (!classId) {
      setMsg({ type: 'error', text: t('evaluationControlCenter.downloads.selectClassFirst', 'Please select a class first.') });
      return;
    }
    if (!selectedEvaluationId) {
      setMsg({ type: 'error', text: t('evaluationControlCenter.downloads.selectEvaluationFirst', 'Please select an evaluation first.') });
      return;
    }
    setDownloading(format);
    try {
      const params = new URLSearchParams({ classId, evaluationId: selectedEvaluationId });
      const url = buildApiUrl(`/api/academics/evaluations/exports/class-results.${format}?${params.toString()}`);
      const resp = await fetch(url, { headers: buildAuthHeaders(), credentials: 'include' });
      if (!resp.ok) {
        let errMsg = `Server error ${resp.status}`;
        try {
          const d = await resp.json();
          const detail = d?.detail ? String(d.detail) : '';
          errMsg = d?.error || errMsg;
          if (detail) errMsg = `${errMsg}: ${detail}`;
        } catch (_) {}
        throw new Error(errMsg);
      }
      const blob = await resp.blob();
      const objectUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = objectUrl;
      const cls = (phaseClasses || []).find(c => String(c.id) === String(classId));
      const clsName = (cls?.name || cls?.className || String(classId)).replace(/[^a-zA-Z0-9]/g, '-');
      const picked = (evaluations || []).find((ev) => String(ev.id) === String(selectedEvaluationId)) || null;
      const evalLabel = (evaluationOptionLabel(t, picked) || '').toString().replace(/\s+/g, '-').replace(/[^a-zA-Z0-9\-]/g, '');
      a.download = `results-${clsName}-${evalLabel}.${format}`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(objectUrl);
      setMsg({ type: 'success', text: `${format.toUpperCase()} download started.` });
    } catch (e) {
      setMsg({ type: 'error', text: e?.message || `Failed to download ${format}` });
    } finally {
      setDownloading(null);
    }
  }, [classId, selectedEvaluationId, phaseClasses, evaluations, buildApiUrl, buildAuthHeaders, t]);

  const selectedClassObj = useMemo(
    () => (phaseClasses || []).find((c) => String(c.id) === String(classId)) || null,
    [phaseClasses, classId]
  );

  const isNurseryMode = useMemo(() => {
    const lvl = (selectedClassObj?.level || '').toString();
    return lvl === 'nursery';
  }, [selectedClassObj]);

  const nurseryRubricScale = useMemo(() => getNurseryRubricScale(t), [i18n.language, t]);

  const resolveGradeLabelForMark = useCallback((mark, scales) => {
    const n = Number(mark);
    if (!Number.isFinite(n) || !Array.isArray(scales) || scales.length === 0) return '';
    const hit = scales.find((s) => {
      const min = Number(s?.minScore);
      const max = Number(s?.maxScore);
      if (!Number.isFinite(min) || !Number.isFinite(max)) return false;
      return n >= min && n <= max;
    });
    if (!hit) return '';
    return String(hit.grade || hit.remarkCode || hit.name || '').trim();
  }, []);

  const levelOptions = useMemo(() => {
    const resolvedPhaseCode = (activePhase || localStorage.getItem('schoolAdmin_activePhase') || localStorage.getItem('activePhase') || '')
      .toString()
      .trim()
      .toLowerCase();
    if (resolvedPhaseCode === 'sec') return ['first_cycle', 'second_cycle'];
    if (resolvedPhaseCode === 'nur_pri') return ['nursery', 'primary'];
    const set = new Set((phaseClasses || []).map((c) => (c?.level || '').toString()).filter(Boolean));
    return Array.from(set);
  }, [activePhase, phaseClasses]);

  useEffect(() => {
    if (level && !levelOptions.includes(level)) setLevel('');
  }, [level, levelOptions]);

  const filteredClasses = useMemo(() => {
    if (!level) return phaseClasses || [];
    return (phaseClasses || []).filter((c) => String(c?.level || '') === String(level));
  }, [phaseClasses, level]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const resp = await fetchJson('/school-setup-and-configuration/school-settings', { method: 'GET' });
        const school = (resp && (resp.data?.school || resp.school || resp.data || resp)) || {};
        const value = school?.passAverage;
        const n = Number(value);
        if (!cancelled) setPassAvg(Number.isFinite(n) ? n : null);
      } catch (_) {
        if (!cancelled) setPassAvg(null);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!selectedClassObj || isNurseryMode) {
        if (!cancelled) setGradeScales([]);
        return;
      }
      try {
        const schoolId = localStorage.getItem('activeSchoolId') || localStorage.getItem('scopeSchoolId') || localStorage.getItem('schoolId') || '';
        const phaseCode = localStorage.getItem('schoolAdmin_activePhase') || localStorage.getItem('activePhase') || '';
        const ayKey = schoolId ? `${schoolId}:currentAcademicYearId` : 'currentAcademicYearId';
        const ayId = localStorage.getItem(ayKey) || '';
        const phaseId = localStorage.getItem('schoolAdmin_activePhaseId') || localStorage.getItem('activePhaseId') || '';
        const ctxHeaders = schoolId
          ? { 'X-Scope-School-Id': schoolId, 'X-Active-Phase': phaseCode, 'X-Academic-Year-Id': ayId, 'X-Active-Phase-Id': phaseId }
          : { 'X-Active-Phase': phaseCode, 'X-Academic-Year-Id': ayId, 'X-Active-Phase-Id': phaseId };

        const classLevel = String(selectedClassObj?.level || '').trim();
        const qs = classLevel ? `?classLevel=${encodeURIComponent(classLevel)}` : '';
        const scales = await fetchJson(`/api/academics/grade-scales${qs}`, { method: 'GET', headers: ctxHeaders }).catch(() => []);
        if (!cancelled) setGradeScales(Array.isArray(scales) ? scales : []);
      } catch (_) {
        if (!cancelled) setGradeScales([]);
      }
    })();
    return () => { cancelled = true; };
  }, [selectedClassObj, isNurseryMode]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!classId) { setSubjects([]); setSubjectId(''); return; }
      try {
        const schoolId = localStorage.getItem('activeSchoolId') || localStorage.getItem('scopeSchoolId') || localStorage.getItem('schoolId') || '';
        const phaseCode = localStorage.getItem('schoolAdmin_activePhase') || localStorage.getItem('activePhase') || '';
        const ayKey = schoolId ? `${schoolId}:currentAcademicYearId` : 'currentAcademicYearId';
        const ayId = localStorage.getItem(ayKey) || '';
        const phaseId = localStorage.getItem('schoolAdmin_activePhaseId') || localStorage.getItem('activePhaseId') || '';
        const headers = schoolId
          ? { 'X-Scope-School-Id': schoolId, 'X-Active-Phase': phaseCode, 'X-Academic-Year-Id': ayId, 'X-Active-Phase-Id': phaseId }
          : { 'X-Active-Phase': phaseCode, 'X-Academic-Year-Id': ayId, 'X-Active-Phase-Id': phaseId };

        const q = `?academicYearId=${encodeURIComponent(ayId)}&phase=${encodeURIComponent(phaseCode)}&schoolId=${encodeURIComponent(schoolId)}`;
        const offered = await fetchJson(`/api/class-subjects/class/${encodeURIComponent(classId)}${q}`, { headers }).catch(() => []);
        let subsRaw = Array.isArray(offered) ? offered : (offered?.classSubjects || offered?.data || []);
        if (!Array.isArray(subsRaw)) subsRaw = [];

        if (!cancelled) {
          setSubjects(subsRaw);
          setSubjectId('');
        }
      } catch (_) {
        if (!cancelled) {
          setSubjects([]);
          setSubjectId('');
        }
      }
    })();
    return () => { cancelled = true; };
  }, [classId]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!classId) {
        if (!cancelled) {
          setEvaluations([]);
          setSelectedEvaluationId('');
        }
        return;
      }
      try {
        const schoolId = localStorage.getItem('activeSchoolId') || localStorage.getItem('scopeSchoolId') || localStorage.getItem('schoolId') || '';
        const phaseCode = localStorage.getItem('schoolAdmin_activePhase') || localStorage.getItem('activePhase') || '';
        const ayKey = schoolId ? `${schoolId}:currentAcademicYearId` : 'currentAcademicYearId';
        const ayId = localStorage.getItem(ayKey) || '';
        const phaseId = localStorage.getItem('schoolAdmin_activePhaseId') || localStorage.getItem('activePhaseId') || '';
        const headers = {
          ...(schoolId ? { 'X-Scope-School-Id': schoolId } : {}),
          ...(phaseCode ? { 'X-Active-Phase': phaseCode } : {}),
          ...(ayId ? { 'X-Academic-Year-Id': ayId } : {}),
          ...(phaseId ? { 'X-Active-Phase-Id': phaseId } : {}),
        };
        const r = await fetchJson(`/api/academics/evaluations/class/${encodeURIComponent(classId)}/evaluations`, { headers });
        const list = Array.isArray(r) ? r : (r?.evaluations || []);
        if (!cancelled) {
          setEvaluations(list);
          setSelectedEvaluationId((prev) => {
            if (prev && list.some((ev) => String(ev.id) === String(prev))) return String(prev);
            return list[0]?.id ? String(list[0].id) : '';
          });
        }
      } catch (_) {
        if (!cancelled) {
          setEvaluations([]);
          setSelectedEvaluationId('');
        }
      }
    })();
    return () => { cancelled = true; };
  }, [classId]);

  const selectedEvaluationObj = useMemo(
    () => (evaluations || []).find((ev) => String(ev.id) === String(selectedEvaluationId)) || null,
    [evaluations, selectedEvaluationId]
  );

  const selectedEvaluationLabel = useMemo(() => {
    if (!selectedEvaluationObj) return '';
    return evaluationOptionLabel(t, selectedEvaluationObj)
      || selectedEvaluationObj.type
      || String(selectedEvaluationObj.id);
  }, [selectedEvaluationObj, t]);

  useEffect(() => {
    setSeqNo(selectedEvaluationObj?.sequenceNo ? String(selectedEvaluationObj.sequenceNo) : '');
  }, [selectedEvaluationObj]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!selectedEvaluationId || !classId || !subjectId) {
        setResult(null);
        setResultRows([]);
        setMeta({});
        setNoRecordedMarks(false);
        return;
      }
      setLoading(true);
      try {
        const schoolId = localStorage.getItem('activeSchoolId') || localStorage.getItem('scopeSchoolId') || localStorage.getItem('schoolId') || '';
        const phaseCode = localStorage.getItem('schoolAdmin_activePhase') || localStorage.getItem('activePhase') || '';
        const ayKey = schoolId ? `${schoolId}:currentAcademicYearId` : 'currentAcademicYearId';
        const ayId = localStorage.getItem(ayKey) || '';
        const phaseId = localStorage.getItem('schoolAdmin_activePhaseId') || localStorage.getItem('activePhaseId') || '';
        const ctxHeaders = schoolId
          ? { 'X-Scope-School-Id': schoolId, 'X-Active-Phase': phaseCode, 'X-Academic-Year-Id': ayId, 'X-Active-Phase-Id': phaseId }
          : { 'X-Active-Phase': phaseCode, 'X-Academic-Year-Id': ayId, 'X-Active-Phase-Id': phaseId };

        if (!selectedEvaluationId) {
          if (!cancelled) setResult({ success: false });
          return;
        }

        const baseEndpoint = isNurseryMode ? '/api/nursery-marks/control-center' : '/api/marks/control-center';
        const markData = await fetchJson(`${baseEndpoint}?classId=${encodeURIComponent(classId)}&subjectId=${encodeURIComponent(subjectId)}&evaluationId=${encodeURIComponent(selectedEvaluationId)}${seqNo ? `&sequenceNo=${encodeURIComponent(seqNo)}` : ''}`, { headers: ctxHeaders }).catch(() => []);
        const list = Array.isArray(markData) ? markData : (markData?.rows || markData?.data || []);

        const rows = (Array.isArray(list) ? list : []).map((m) => {
          const sid = m?.Enrollment?.studentId ?? m?.Enrollment?.Student?.id ?? m?.studentId;
          const firstName = m?.Enrollment?.Student?.firstName || '';
          const lastName = m?.Enrollment?.Student?.lastName || '';
          const studentName = `${firstName} ${lastName}`.trim() || (sid ? t('evaluationControlCenter.common.studentWithId', { id: sid }) : t('evaluationControlCenter.student'));
          const mark = isNurseryMode ? (m?.rating ?? m?.mark ?? null) : (m?.score ?? m?.mark ?? null);
          return {
            studentId: String(sid || ''),
            studentName,
            mark,
            grade: m?.grade || resolveGradeLabelForMark(mark, gradeScales) || ''
          };
        }).sort((a, b) => String(a.studentName).localeCompare(String(b.studentName)));

        if (!cancelled) {
          setResultRows(rows);
          setNoRecordedMarks(rows.length === 0);
          setMeta({ type: selectedEvaluationObj?.type || 'evaluation', number: seqNo || null, name: selectedEvaluationLabel || null });
          setResult({ success: true });
        }
      } catch (e) {
        if (!cancelled) {
          setMsg({ type: 'error', text: e?.message || t('evaluationControlCenter.results.errors.loadFail') });
          setResult({ success: false });
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [selectedEvaluationId, selectedEvaluationObj, selectedEvaluationLabel, seqNo, classId, subjectId, isNurseryMode, gradeScales, t, resolveGradeLabelForMark]);

  return (
    <Card>
      <CardContent>
        <Typography variant="h6" gutterBottom>{t('evaluationControlCenter.results.title')}</Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth>
              <InputLabel id="level-select-results">{t('evaluationControlCenter.common.level')}</InputLabel>
              <Select
                labelId="level-select-results"
                label={t('evaluationControlCenter.common.level')}
                value={level}
                onChange={(e) => {
                  setLevel(e.target.value);
                  setClassId('');
                  setSelectedEvaluationId('');
                  setSubjectId('');
                  setResult(null);
                }}
              >
                <MenuItem value=""><em>{t('evaluationControlCenter.common.allLevels')}</em></MenuItem>
                {levelOptions.map((lvl) => (
                  <MenuItem key={lvl} value={lvl}>{levelLabel(t, lvl)}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth>
              <InputLabel id="class-select-results">{t('evaluationControlCenter.common.class')}</InputLabel>
              <Select
                labelId="class-select-results"
                label={t('evaluationControlCenter.common.class')}
                value={classId}
                onChange={(e) => {
                  setClassId(e.target.value);
                  setSelectedEvaluationId('');
                  setSubjectId('');
                  setResult(null);
                }}
              >
                <MenuItem value=""><em>{t('evaluationControlCenter.common.selectClass')}</em></MenuItem>
                {filteredClasses.map(c => (
                  <MenuItem key={c.id} value={c.id}>{c.name || c.className || t('evaluationControlCenter.common.classWithId', { id: c.id })}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth disabled={!classId}>
              <InputLabel id="evaluation-select-results">{t('evaluationControlCenter.common.evaluation', 'Evaluation')}</InputLabel>
              <Select
                labelId="evaluation-select-results"
                label={t('evaluationControlCenter.common.evaluation', 'Evaluation')}
                value={selectedEvaluationId}
                onChange={(e) => {
                  setSelectedEvaluationId(e.target.value);
                  setSubjectId('');
                  setResult(null);
                }}
              >
                <MenuItem value=""><em>{t('evaluationControlCenter.common.selectEvaluation', 'Select evaluation')}</em></MenuItem>
                {(evaluations || []).map((ev) => (
                  <MenuItem key={ev.id} value={String(ev.id)}>
                    {evaluationOptionLabel(t, ev) || (ev.type || String(ev.id))}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth disabled={!classId || !selectedEvaluationId}>
              <InputLabel id="subject-select-results">{t('evaluationControlCenter.common.subject')}</InputLabel>
              <Select
                labelId="subject-select-results"
                label={t('evaluationControlCenter.common.subject')}
                value={subjectId}
                onChange={(e) => {
                  setSubjectId(e.target.value);
                  setResult(null);
                }}
              >
                <MenuItem value=""><em>{t('evaluationControlCenter.common.selectSubject')}</em></MenuItem>
                {subjects.map((cs) => {
                  const subj = cs.Subject || cs.subject;
                  const sid = subj?.id || cs.subjectId || '';
                  const label = subj?.name || (sid ? t('evaluationControlCenter.common.subjectWithId', { id: sid }) : t('evaluationControlCenter.common.subject'));
                  return (
                    <MenuItem key={cs.id} value={sid}>{label}</MenuItem>
                  );
                })}
              </Select>
            </FormControl>
          </Grid>
        </Grid>

        <Divider sx={{ my: 2 }} />

        {(classId && selectedEvaluationId) && (
          <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', mb: 2 }}>
            <Button
              size="small"
              variant="contained"
              color="error"
              disabled={!!downloading}
              onClick={() => downloadResults('pdf')}
            >
              {downloading === 'pdf' ? t('evaluationControlCenter.downloads.downloading', 'Downloading…') : t('evaluationControlCenter.downloads.downloadPdf', 'Download PDF')}
            </Button>
            <Button
              size="small"
              variant="outlined"
              color="success"
              disabled={!!downloading}
              onClick={() => downloadResults('xlsx')}
            >
              {downloading === 'xlsx' ? t('evaluationControlCenter.downloads.downloading', 'Downloading…') : t('evaluationControlCenter.downloads.downloadXlsx', 'Download XLSX')}
            </Button>
          </Box>
        )}

        {loading ? (
          <Box sx={{ py: 2 }}>
            <AppLoader
              variant="circular3"
              size={56}
              messages={[
                t('evaluationControlCenter.results.loader.loadingResults'),
                t('evaluationControlCenter.results.loader.buildingTable'),
                t('evaluationControlCenter.results.loader.applyingPhaseAy')
              ]}
            />
          </Box>
        ) : result && result.success ? (
          <>
            {noRecordedMarks && (
              <Alert severity="info" sx={{ mb: 2 }}>
                {t('evaluationControlCenter.results.noRecordedMarks')}
              </Alert>
            )}
            {Object.keys(meta).length > 0 && (
              <Box sx={{ mb: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                {meta.type && <Chip size="small" label={`${t('evaluationControlCenter.results.type')}: ${meta.name || `${meta.type}${meta.number ? ` ${meta.number}` : ''}`}`} />}
              </Box>
            )}
            <TableContainer component={Paper} variant="outlined" sx={{ width: '100%', overflowX: 'auto' }}>
              {/* The results view, read-only, and the same rule: a flat 520 is
                  wider than a phone. Nothing here needs to be dragged sideways
                  to be read. */}
              <Table
                size="small"
                sx={{
                  minWidth: { xs: 0, sm: isNurseryMode ? 760 : 520 },
                  tableLayout: { xs: 'fixed', sm: 'auto' },
                }}
              >
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ width: { xs: isNurseryMode ? '40%' : '52%', sm: 'auto' }, px: { xs: 0.75, sm: 2 } }}>
                      <strong>{t('evaluationControlCenter.student')}</strong>
                    </TableCell>
                    {isNurseryMode ? (
                      nurseryRubricScale.map((g) => (
                        <TableCell key={g.value} align="center" sx={{ backgroundColor: g.bg, py: 1.25, width: { xs: '12%', sm: 'auto' }, px: { xs: 0.25, sm: 2 } }}>
                          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', lineHeight: 1.1, gap: 0.5 }}>
                            {renderNurseryRubricBadge(g)}
                            <Typography variant="caption" sx={{ color: g.text, fontWeight: 700, display: 'block', textAlign: 'center', lineHeight: 1.1 }}>{g.value}</Typography>
                          </Box>
                        </TableCell>
                      ))
                    ) : (
                      <>
                        <TableCell sx={{ width: { xs: '24%', sm: 'auto' }, px: { xs: 0.75, sm: 2 } }}>
                          <strong>{t('evaluationControlCenter.results.markAvg')}</strong>
                        </TableCell>
                        <TableCell sx={{ width: { xs: '24%', sm: 'auto' }, px: { xs: 0.75, sm: 2 } }}>
                          <strong>{t('evaluationControlCenter.results.grade')}</strong>
                        </TableCell>
                      </>
                    )}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(resultRows || []).map((r) => {
                    const n = Number(r.mark);
                    const val = Number.isFinite(n) ? Math.round(n) : null;
                    return (
                      <TableRow key={r.studentId}>
                        <TableCell>{r.studentName}</TableCell>
                        {isNurseryMode ? (
                          nurseryRubricScale.map((g) => {
                            const selected = val != null && val === g.value;
                            return (
                              <TableCell
                                key={g.value}
                                align="center"
                                sx={{
                                  backgroundColor: selected ? g.bd : g.bg,
                                  color: selected ? '#fff' : g.text,
                                  fontWeight: 900,
                                  border: selected ? `2px solid ${g.bd}` : '1px solid rgba(0,0,0,0.08)'
                                }}
                              >
                                {selected ? '✓' : ''}
                              </TableCell>
                            );
                          })
                        ) : (
                          <>
                            <TableCell sx={(() => {
                              if (!Number.isFinite(n)) return undefined;
                              if (Number.isFinite(passAvg) && n < passAvg) return { color: 'error.main', fontWeight: 700 };
                              return undefined;
                            })()}>
                              {Number.isFinite(Number(r.mark)) ? Number(r.mark).toFixed(2) : '—'}
                            </TableCell>
                            <TableCell>{r.grade || '—'}</TableCell>
                          </>
                        )}
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        ) : (
          <Alert severity="info">{t('evaluationControlCenter.results.selectSequenceClassSubject', { defaultValue: 'Select a class, evaluation and subject to view results.' })}</Alert>
        )}
      </CardContent>
      <Snackbar open={!!msg} autoHideDuration={3000} onClose={() => setMsg(null)}>
        {msg && <Alert severity={msg.type} onClose={() => setMsg(null)}>{msg.text}</Alert>}
      </Snackbar>
    </Card>
  );
}

function StudentSubjectSelection() {
  const { t } = useTranslation();
  const { activePhase } = usePhase();
  const { classes: phaseClasses, loading: classesLoading } = usePhaseClasses({ allowEmptyFallback: false });
  const [classId, setClassId] = useState('');
  const [students, setStudents] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [selectionMap, setSelectionMap] = useState({});
  const [originalMap, setOriginalMap] = useState({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);

  const buildContext = useCallback(() => {
    const schoolId = localStorage.getItem('activeSchoolId') || localStorage.getItem('scopeSchoolId') || localStorage.getItem('schoolId') || '';
    const phaseCode = localStorage.getItem('schoolAdmin_activePhase') || localStorage.getItem('activePhase') || activePhase || '';
    const phaseId = localStorage.getItem('schoolAdmin_activePhaseId') || localStorage.getItem('activePhaseId') || '';
    const ayFallbackKey = schoolId ? `${schoolId}:currentAcademicYearId` : 'currentAcademicYearId';
    const ayId = getCurrentAcademicYearId() || localStorage.getItem(ayFallbackKey) || '';
    const headers = {
      ...(schoolId ? { 'X-Scope-School-Id': schoolId } : {}),
      ...(phaseCode ? { 'X-Active-Phase': phaseCode } : {}),
      ...(ayId ? { 'X-Academic-Year-Id': ayId } : {}),
      ...(phaseId ? { 'X-Active-Phase-Id': phaseId } : {}),
    };
    const q = `?academicYearId=${encodeURIComponent(ayId)}&phase=${encodeURIComponent(phaseCode)}&schoolId=${encodeURIComponent(schoolId)}`;
    return { headers, q };
  }, [activePhase]);

  const normalizeMap = useCallback((rawMap) => {
    const next = {};
    Object.entries(rawMap || {}).forEach(([studentId, subjectIds]) => {
      next[String(studentId)] = Array.from(new Set((Array.isArray(subjectIds) ? subjectIds : []).map((value) => String(value))));
    });
    return next;
  }, []);

  useEffect(() => {
    if (classId && (phaseClasses || []).some((row) => String(row?.id) === String(classId))) return;
    const preferred = (phaseClasses || []).find((row) => String(row?.level || '').toLowerCase() !== 'nursery') || phaseClasses?.[0] || null;
    setClassId(preferred?.id ? String(preferred.id) : '');
  }, [phaseClasses, classId]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!classId) {
        setStudents([]);
        setSubjects([]);
        setSelectionMap({});
        setOriginalMap({});
        return;
      }
      setLoading(true);
      try {
        const { headers, q } = buildContext();
        const [stu, offered, enroll] = await Promise.all([
          fetchJson(`/api/allocations/students/class/${encodeURIComponent(classId)}`, { headers }).catch(() => ({ students: [] })),
          fetchJson(`/api/class-subjects/class/${encodeURIComponent(classId)}${q}`, { headers }).catch(() => []),
          fetchJson(`/api/allocations/student-subjects/class/${encodeURIComponent(classId)}${q}`, { headers }).catch(() => ({ map: {} })),
        ]);
        if (cancelled) return;

        const studentRows = Array.isArray(stu?.students) ? stu.students : [];
        let subjectRows = Array.isArray(offered) ? offered : (offered?.classSubjects || offered?.data || []);
        if (!Array.isArray(subjectRows)) subjectRows = [];
        const mappedSubjects = subjectRows.map((item) => {
          const subject = item?.Subject || item?.subject || {};
          return {
            id: subject.id ?? item?.subjectId ?? item?.id,
            name: subject.name || item?.name || item?.code || t('evaluationControlCenter.studentSubjectSelection.subjectFallback', { defaultValue: 'Subject' }),
            code: subject.code || item?.code || ''
          };
        }).filter((item) => item && item.id != null);

        const seen = new Set();
        const uniqueSubjects = [];
        mappedSubjects.forEach((item) => {
          const key = String(item.id);
          if (!seen.has(key)) {
            seen.add(key);
            uniqueSubjects.push(item);
          }
        });

        const nextMap = normalizeMap(enroll?.map || {});
        setStudents(studentRows);
        setSubjects(uniqueSubjects);
        setSelectionMap(nextMap);
        setOriginalMap(nextMap);
      } catch (error) {
        if (!cancelled) {
          setStudents([]);
          setSubjects([]);
          setSelectionMap({});
          setOriginalMap({});
          setMsg({ type: 'error', text: error?.message || t('evaluationControlCenter.studentSubjectSelection.loadFail', { defaultValue: 'Failed to load student subject selections.' }) });
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [classId, buildContext, normalizeMap, t]);

  const hasChanges = useMemo(() => {
    const studentIds = new Set([
      ...Object.keys(selectionMap || {}),
      ...Object.keys(originalMap || {}),
      ...(students || []).map((row) => String(row?.id)).filter(Boolean)
    ]);
    for (const sid of studentIds) {
      const before = Array.from(new Set((originalMap[sid] || []).map(String))).sort();
      const after = Array.from(new Set((selectionMap[sid] || []).map(String))).sort();
      if (before.length !== after.length) return true;
      if (before.some((value, index) => value !== after[index])) return true;
    }
    return false;
  }, [selectionMap, originalMap, students]);

  const handleToggle = useCallback((studentId, subjectId, checked) => {
    const sid = String(studentId);
    const subjectKey = String(subjectId);
    setSelectionMap((prev) => {
      const current = Array.from(new Set((prev[sid] || []).map(String)));
      const next = checked
        ? Array.from(new Set([...current, subjectKey]))
        : current.filter((value) => value !== subjectKey);
      return { ...prev, [sid]: next };
    });
  }, []);

  const handleSave = useCallback(async () => {
    if (!classId) return;
    const toAdd = [];
    const toRemove = [];
    const studentIds = new Set([
      ...Object.keys(selectionMap || {}),
      ...Object.keys(originalMap || {}),
      ...(students || []).map((row) => String(row?.id)).filter(Boolean)
    ]);

    studentIds.forEach((sid) => {
      const before = Array.from(new Set((originalMap[sid] || []).map(String)));
      const after = Array.from(new Set((selectionMap[sid] || []).map(String)));
      after.filter((value) => !before.includes(value)).forEach((subjectId) => {
        toAdd.push({ classId: Number(classId), studentId: Number(sid), subjectId: Number(subjectId), source: 'admin' });
      });
      before.filter((value) => !after.includes(value)).forEach((subjectId) => {
        toRemove.push({ studentId: Number(sid), subjectId: Number(subjectId) });
      });
    });

    if (toAdd.length === 0 && toRemove.length === 0) {
      setMsg({ type: 'info', text: t('evaluationControlCenter.studentSubjectSelection.noChanges', { defaultValue: 'No changes to save.' }) });
      return;
    }

    setSaving(true);
    try {
      const { headers } = buildContext();
      const requestHeaders = { 'Content-Type': 'application/json', ...headers };
      if (toAdd.length) {
        await fetchJson('/api/allocations/student-subjects/bulk', { method: 'POST', headers: requestHeaders, body: JSON.stringify(toAdd) });
      }
      if (toRemove.length) {
        await fetchJson('/api/allocations/student-subjects/bulk', { method: 'DELETE', headers: requestHeaders, body: JSON.stringify({ classId: Number(classId), pairs: toRemove }) });
      }
      const snapshot = normalizeMap(selectionMap);
      setOriginalMap(snapshot);
      setSelectionMap(snapshot);
      setMsg({ type: 'success', text: t('evaluationControlCenter.studentSubjectSelection.saveSuccess', { defaultValue: 'Student subject selections saved.' }) });
    } catch (error) {
      setMsg({ type: 'error', text: error?.message || t('evaluationControlCenter.studentSubjectSelection.saveFail', { defaultValue: 'Failed to save student subject selections.' }) });
    } finally {
      setSaving(false);
    }
  }, [classId, selectionMap, originalMap, students, buildContext, normalizeMap, t]);

  if (String(activePhase || '').toLowerCase() !== 'sec') {
    return <Alert severity="info">{t('evaluationControlCenter.studentSubjectSelection.secondaryOnly', { defaultValue: 'Student subject selection is only available for the secondary phase.' })}</Alert>;
  }

  return (
    <Card>
      <CardContent>
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'stretch', md: 'center' }, gap: 2, mb: 2 }}>
          <Typography variant="h6">{t('evaluationControlCenter.tabs.studentSubjectSelection')}</Typography>
          <Button variant="contained" onClick={handleSave} disabled={!classId || loading || saving || !hasChanges}>
            {saving ? t('evaluationControlCenter.saving') : t('evaluationControlCenter.save')}
          </Button>
        </Box>

        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={12} md={4}>
            <FormControl fullWidth disabled={classesLoading || loading}>
              <InputLabel id="student-subject-selection-class-label">{t('evaluationControlCenter.common.class', 'Class')}</InputLabel>
              <Select
                labelId="student-subject-selection-class-label"
                label={t('evaluationControlCenter.common.class', 'Class')}
                value={classId}
                onChange={(e) => setClassId(String(e.target.value))}
              >
                <MenuItem value=""><em>{t('evaluationControlCenter.common.selectClass', 'Select class')}</em></MenuItem>
                {(phaseClasses || []).map((cls) => (
                  <MenuItem key={cls.id} value={String(cls.id)}>{cls.name || cls.className || `Class ${cls.id}`}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>

        {!classId ? (
          <Alert severity="info">{t('evaluationControlCenter.studentSubjectSelection.selectClassPrompt', { defaultValue: 'Select a class to manage student subject selections.' })}</Alert>
        ) : loading ? (
          <AppLoader />
        ) : students.length === 0 ? (
          <Alert severity="info">{t('evaluationControlCenter.studentSubjectSelection.noStudents', { defaultValue: 'No students found for this class.' })}</Alert>
        ) : subjects.length === 0 ? (
          <Alert severity="info">{t('evaluationControlCenter.studentSubjectSelection.noSubjects', { defaultValue: 'No subjects are assigned to this class.' })}</Alert>
        ) : (
          <TableContainer component={Paper} variant="outlined">
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>{t('evaluationControlCenter.studentSubjectSelection.columns.rollNo', { defaultValue: 'Roll No' })}</TableCell>
                  <TableCell>{t('evaluationControlCenter.studentSubjectSelection.columns.student', { defaultValue: 'Student' })}</TableCell>
                  {subjects.map((subject) => (
                    <TableCell key={subject.id} align="center">{subject.code || subject.name}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {students.map((student) => {
                  const selected = new Set((selectionMap[String(student.id)] || []).map(String));
                  const rollNo = student.studentNumber || student.rollNo || student.studentNo || '';
                  const studentName = `${student.firstName || ''} ${student.lastName || ''}`.trim() || student.name || t('evaluationControlCenter.student');
                  return (
                    <TableRow key={student.id} hover>
                      <TableCell>{rollNo || '—'}</TableCell>
                      <TableCell>{studentName}</TableCell>
                      {subjects.map((subject) => (
                        <TableCell key={`${student.id}-${subject.id}`} align="center" padding="checkbox">
                          <Checkbox
                            checked={selected.has(String(subject.id))}
                            onChange={(e) => handleToggle(student.id, subject.id, e.target.checked)}
                            disabled={saving}
                          />
                        </TableCell>
                      ))}
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </CardContent>
      <Snackbar open={!!msg} autoHideDuration={4000} onClose={() => setMsg(null)}>
        {msg && <Alert severity={msg.type} onClose={() => setMsg(null)}>{msg.text}</Alert>}
      </Snackbar>
    </Card>
  );
}


/**
 * ONE SUBJECT, AND WHICH CLASSES ARE BEHIND ON IT.
 *
 * The summary line answers "is this subject done"; the classes answer "then
 * whose sheet am I chasing", which is the question that actually gets somebody
 * out of their chair. Rolled up to the subject and expandable rather than laid
 * out flat, because a secondary school has around thirty subjects across a
 * dozen classes and the flat grid is three hundred rows nobody reads.
 *
 * Outstanding classes are listed FIRST and the finished ones after. A list
 * sorted alphabetically buries the four classes you need among the twenty you
 * do not.
 */
function SubjectCoverageRow({ subject: s, t, statusWord, tone, evalTypeId }) {
  const [open, setOpen] = React.useState(false);

  // Behind first, then done; each group by name so the order is stable between
  // renders and between sequences.
  const classes = React.useMemo(() => {
    const rank = (c) => (c.status === 'filled' ? 1 : 0);
    return [...(s.classes || [])].sort(
      (a, b) => rank(a) - rank(b) || String(a.className).localeCompare(String(b.className)),
    );
  }, [s.classes]);

  return (
    <>
      <TableRow
        hover
        onClick={() => setOpen((v) => !v)}
        sx={{ cursor: 'pointer', '& > *': { borderBottom: open ? 'unset' : undefined } }}
      >
        <TableCell>
          {s.subjectName}
          {/* The state in words as well as in colour. Red and green is the one
              pair a colourblind reader cannot separate, and this column exists
              entirely to tell three states apart. */}
          <Typography variant="caption" display="block" color="text.secondary">
            {statusWord(s.status)}
            {s.missing > 0 ? ' - ' + t('evaluationControlCenter.coverage.missing', { count: s.missing, defaultValue: s.missing + ' missing' }) : ''}
          </Typography>
        </TableCell>
        <TableCell align="right">
          <Chip size="small" color={tone(s.status)}
            variant={s.status === 'filled' ? 'filled' : 'outlined'}
            label={s.recorded + '/' + s.expected} />
        </TableCell>
        <TableCell align="right">{s.classesFilled}/{s.classesTotal}</TableCell>
      </TableRow>

      <TableRow>
        <TableCell colSpan={3} sx={{ py: 0, border: 0 }}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box sx={{ py: 1, pl: 1 }}>
              {classes.map((c) => (
                <Box key={c.classId}
                  sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1, py: 0.4 }}>
                  <Typography variant="body2" sx={{
                    flex: 1,
                    color: c.status === 'filled' ? 'text.secondary' : 'text.primary',
                  }}>
                    {c.className}
                  </Typography>
                  <Chip size="small" color={tone(c.status)}
                    variant={c.status === 'filled' ? 'filled' : 'outlined'}
                    label={c.recorded + '/' + c.expected} />
                  {/* THE SHEET FOR EXACTLY THIS ROW. A class and a subject and
                      an evaluation is precisely what a submission sheet is, and
                      this row already names all three — so the download belongs
                      here rather than behind three more pickers somewhere else.

                      A plain link, not a fetch: the server answers with a PDF
                      and a Content-Disposition, and letting the browser handle
                      that gives a real download with a progress bar instead of
                      a blob assembled in memory. */}
                  <Tooltip title={t('evaluationControlCenter.coverage.downloadSheet', { defaultValue: 'Submission sheet (PDF)' })}>
                    <IconButton
                      size="small"
                      component="a"
                      href={`${API_BASE}/api/marks/submission-sheet.pdf?classId=${encodeURIComponent(c.classId)}&subjectId=${encodeURIComponent(s.subjectId)}&evaluationTypeId=${encodeURIComponent(evalTypeId)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <DownloadIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              ))}
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
}

/**
 * SUBJECTS WITH MARKS — what is in, what is part-done, what has not been started.
 *
 * An administrator chasing a sequence had no way to see what was outstanding
 * except to open the mark sheet for every class and every subject in turn. In a
 * secondary school that is a few hundred screens, so nobody checked and the
 * gaps surfaced when the report cards came out wrong.
 *
 * THE PHASE IS NOT A CONTROL HERE. There is a phase switcher in the bar above
 * every page and the server scopes this to it like every other screen; a second
 * phase picker inside one tab would only be a way for the two to disagree.
 * Level, sequence and status are the questions this screen actually asks.
 *
 * PART-DONE IS THE STATE THAT MATTERS. A class where the teacher entered twenty
 * of twenty-four is not finished, and anything reporting it as finished is how
 * four children reach a report card with a blank where a grade belongs. So
 * "completely filled" means every student who offers the subject has a mark,
 * not that somebody has started.
 */
function MarkCoverage() {
  const { t } = useTranslation();
  // THE SCHOOL'S OWN EVALUATIONS, not 1 to 6.
  //
  // A school owns its evaluation list: six sequences, but also "Pre-Mock 1",
  // "Pre-Mock 2", "Mock" and whatever else it has added. A hard-coded number
  // picker could name none of those, and a mock is the evaluation an
  // administrator most needs to chase. Same endpoint the Evaluation Settings
  // tab reads, so the two offer the same list in the same order.
  const [evalTypes, setEvalTypes] = useState([]);
  const [evalTypeId, setEvalTypeId] = useState('');
  const [level, setLevel] = useState('');
  const [status, setStatus] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const STATUSES = useMemo(() => ([
    { value: '', label: t('evaluationControlCenter.coverage.statusAll', { defaultValue: 'All' }) },
    { value: 'filled', label: t('evaluationControlCenter.coverage.statusFilled', { defaultValue: 'Completely filled' }) },
    { value: 'partial', label: t('evaluationControlCenter.coverage.statusPartial', { defaultValue: 'Partially filled' }) },
    { value: 'empty', label: t('evaluationControlCenter.coverage.statusEmpty', { defaultValue: 'Not filled' }) },
  ]), [t]);

  useEffect(() => {
    let alive = true;
    fetchJson('/api/academics/evaluations/setup/types')
      .then((res) => {
        if (!alive) return;
        const list = Array.isArray(res?.types) ? res.types : [];
        setEvalTypes(list);
        // Open on whichever is OPEN — that is the one being marked now, and
        // therefore the one somebody came here to chase.
        setEvalTypeId((cur) => cur || String((list.find((x) => x.state === 'open') || list[0] || {}).id || ''));
      })
      .catch(() => { if (alive) setEvalTypes([]); });
    return () => { alive = false; };
  }, []);

  useEffect(() => {
    if (!evalTypeId) return undefined;
    let alive = true;
    setLoading(true);
    setError('');
    const qs = new URLSearchParams({ evaluationTypeId: String(evalTypeId) });
    if (level) qs.set('level', level);
    if (status) qs.set('status', status);
    fetchJson('/api/marks/coverage?' + qs.toString())
      .then((res) => { if (alive) setData(res); })
      .catch((e) => {
        if (alive) setError(e?.message || t('evaluationControlCenter.coverage.loadFail', { defaultValue: 'Could not load this.' }));
      })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [evalTypeId, level, status, t]);

  const levels = data?.levels || [];
  // From the response, so the picker only offers levels that exist in the phase
  // currently in force — and keeps offering the one in use even while a status
  // filter has emptied the list below it.
  const levelOptions = data?.allLevels || [];
  const shown = levels.reduce((n, l) => n + l.subjects.length, 0);

  const tone = (st) => (st === 'filled' ? 'success' : (st === 'partial' ? 'warning' : 'default'));
  const statusWord = (st) => {
    const found = STATUSES.find((x) => x.value === st);
    return found ? found.label : st;
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap', mb: 2 }}>
        <TextField select size="small" sx={{ minWidth: 150 }}
          label={t('evaluationControlCenter.coverage.level', { defaultValue: 'Level' })}
          value={level} onChange={(e) => setLevel(e.target.value)}>
          <MenuItem value="">{t('evaluationControlCenter.coverage.allLevels', { defaultValue: 'All levels' })}</MenuItem>
          {levelOptions.map((lv) => <MenuItem key={lv} value={lv}>{levelLabel(t, lv)}</MenuItem>)}
        </TextField>

        <TextField select size="small" sx={{ minWidth: 200 }}
          label={t('evaluationControlCenter.evaluation', { defaultValue: 'Evaluation' })}
          value={evalTypeId} onChange={(e) => setEvalTypeId(e.target.value)}>
          {evalTypes.map((x) => (
            <MenuItem key={x.id} value={String(x.id)}>
              {x.name}
              {x.state === 'open' ? ' · ' + t('evaluationControlCenter.coverage.openNow', { defaultValue: 'open' }) : ''}
            </MenuItem>
          ))}
        </TextField>

        <TextField select size="small" sx={{ minWidth: 200 }}
          label={t('evaluationControlCenter.coverage.status', { defaultValue: 'Status' })}
          value={status} onChange={(e) => setStatus(e.target.value)}>
          {STATUSES.map((o) => <MenuItem key={o.value || 'all'} value={o.value}>{o.label}</MenuItem>)}
        </TextField>

        {loading && <CircularProgress size={20} />}
        {!loading && !error && (
          <Typography variant="body2" color="text.secondary">
            {t('evaluationControlCenter.coverage.summary', { count: shown, defaultValue: shown + ' subject(s)' })}
          </Typography>
        )}
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {!loading && !error && levels.length === 0 && (
        <Alert severity="info">
          {t('evaluationControlCenter.coverage.none', {
            defaultValue: 'Nothing matches those filters for this sequence.',
          })}
        </Alert>
      )}

      <Grid container spacing={2}>
        {levels.map((lvl) => (
          <Grid item xs={12} md={6} key={lvl.level}>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
                  {levelLabel(t, lvl.level)}
                </Typography>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>
                        <strong>{t('evaluationControlCenter.subject')}</strong>
                        <Typography variant="caption" display="block" color="text.secondary">
                          {t('evaluationControlCenter.coverage.tapForClasses', { defaultValue: 'Tap a subject to see its classes' })}
                        </Typography>
                      </TableCell>
                      <TableCell align="right"><strong>{t('evaluationControlCenter.coverage.entered', { defaultValue: 'Entered' })}</strong></TableCell>
                      <TableCell align="right"><strong>{t('evaluationControlCenter.coverage.classes', { defaultValue: 'Classes' })}</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {lvl.subjects.map((s) => (
                      <SubjectCoverageRow key={s.subjectId} subject={s} t={t}
                        statusWord={statusWord} tone={tone} evalTypeId={evalTypeId} />
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
}

export default function EvaluationControlCenterPage() {
  const { t } = useTranslation();
  const { activePhase, getPhaseLabel } = usePhase();
  const isSecondaryPhase = String(activePhase || '').toLowerCase() === 'sec';

  const tabs = useMemo(() => {
    const base = [
      { key: 'evaluationSettings', label: t('evaluationControlCenter.tabs.evaluationSettings') },
      // Custom evaluations are secondary-only: evaluationTypeController refuses
      // creation outside the sec phase with 403 "Mock evaluations are only
      // available in the sec phase." The tab used to show in nur_pri too, where
      // every action on it could only fail.
      ...(isSecondaryPhase
        ? [{ key: 'customEvaluations', label: t('evaluationControlCenter.tabs.customEvaluations') }]
        : []),
      { key: 'markEntry', label: t('evaluationControlCenter.tabs.markEntry') },
      ...(isSecondaryPhase
        ? [{ key: 'studentSubjectSelection', label: t('evaluationControlCenter.tabs.studentSubjectSelection') }]
        : []),
      { key: 'results', label: t('evaluationControlCenter.tabs.results') },
      // Last, because it is the tab you open to CHECK the work the other
      // tabs do rather than to do any of it.
      { key: 'coverage', label: t('evaluationControlCenter.tabs.coverage', { defaultValue: 'Subjects with marks' }) }
    ];
    return base;
  }, [isSecondaryPhase, t]);

  const [tabKey, setTabKey] = useState('evaluationSettings');
  const tabIndex = useMemo(() => {
    const idx = tabs.findIndex((x) => x.key === tabKey);
    return idx >= 0 ? idx : 0;
  }, [tabs, tabKey]);

  useEffect(() => {
    // If phase switches away from Secondary while that tab is selected, move to Mark Entry
    if (!isSecondaryPhase && tabKey === 'studentSubjectSelection') {
      setTabKey('markEntry');
    }
  }, [isSecondaryPhase, tabKey]);

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: { xs: 'flex-start', sm: 'center' }, flexWrap: 'wrap', gap: 1.5, mb: 2 }}>
        <Typography variant="h4" sx={{ fontWeight: 700 }}>{t('evaluationControlCenter.title')}</Typography>
        {/* The phase chip that sat here is gone — the scope bar above every
            page already names the phase and the year. */}
      </Box>

      <Tabs
        value={tabIndex}
        onChange={(e, v) => {
          const next = tabs[v];
          if (next?.key) setTabKey(next.key);
        }}
        variant="scrollable"
        scrollButtons="auto"
        allowScrollButtonsMobile
        sx={{ mb: 3 }}
      >
        {tabs.map((tb) => (
          <Tab key={tb.key} label={tb.label} />
        ))}
      </Tabs>

      {tabKey === 'evaluationSettings' && (
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <PassAverageSettings />
          </Grid>
          {String(activePhase || '').toLowerCase() === 'sec' && (
            <Grid item xs={12}>
              <MeritThresholdSettings />
            </Grid>
          )}
          <Grid item xs={12}>
            <PromotionCriteriaSettings />
          </Grid>
          <Grid item xs={12}>
            <SetupTypesManager />
          </Grid>
        </Grid>
      )}

      {tabKey === 'customEvaluations' && (
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <AssignmentsManager />
          </Grid>
        </Grid>
      )}

      {tabKey === 'markEntry' && (
        <MarkEntryAdmin />
      )}

      {tabKey === 'studentSubjectSelection' && isSecondaryPhase && (
        <StudentSubjectSelection />
      )}

      {tabKey === 'results' && (
        <ResultsViewer />
      )}

      {tabKey === 'coverage' && (
        <MarkCoverage />
      )}
    </Box>
  );
}